/**
 * KeybookView — credential manager, drawn as a USB-KEY GALLERY on the
 * amber keybook stage (DESIGN_DOC §10, the frosted-glass kit).
 *
 * Every key is a frosted-glass USB fob (`.kbg-key` — the ws-project-card
 * layer stack with a gold-pin connector head hanging off the left edge,
 * per-key hue hashed from its label). Collections render as FOLDER tiles
 * (`.ws-home-tile`) in a strip above the keys; clicking one opens a
 * focused folder view (back + share). Keys are grouped by OWNERSHIP:
 * "Your keys", "Shared with you", and — only while an admin or the owner
 * turns on "Show as admin" (as the drive's Projects / PDApps pages) — "Rest
 * of the org": the keys they read only through that role. What's genuinely
 * shared to you never blurs into what you merely oversee as admin.
 * Selecting a key opens a CENTERED glass modal (`.ws-glass-dialog`).
 * Separation everywhere is fill + carved wells + the one glass hairline;
 * no drawn borders (TL;DR rule 7).
 *
 * Server-side encryption: list/create/read all just talk to the API.
 * No browser crypto setup, no passkey gate, no key derivation.
 *
 * The detail modal covers the full keybook API surface:
 *   - Key ID (`ent_…`) as a compact copyable chip — the external handle
 *     PDApps and scripts bind credentials by (cred_to_env stores it
 *     internally; the PDApp grant flow references it).
 *   - Secret fields (lazy decrypt on Reveal), GitHub install summary.
 *   - Personal nickname (per-member custom label).
 *   - Access (KeyAccessDialog — people/teams/roles today, PDApp +
 *     outsider views staged), ownership transfer/claim, delete.
 *   - Collection filing.
 *   - File bindings with per-binding health.
 *   - Activity timeline (entry audit log, manage-only).
 *
 * e2e contract (e2e/keybook/*.spec.ts) — pinned, do not rename:
 *   keybook-list (+aria-busy) · keybook-show-admin (role=switch,
 *   aria-checked) · collection-add (root managers; opens the inline
 *   input with placeholder "New collection name…", Enter creates) ·
 *   collection-folder (a tile — a container of two sibling buttons:
 *   collection-open and the ⋯ collection-menu, revealed on hover or focus,
 *   whose glass menu holds collection-share / collection-rename /
 *   collection-delete for Manage, collection-access (read-only) for Write or
 *   manage_keybook_permission) · key-card (a container; its name slot
 *   key-card-name is the fob's button, the share control a sibling button) ·
 *   action-access-view (the read-only Access in the key detail) ·
 *   entry rows hold a BUTTON (the name slot) whose text contains the label · entry-mine-badge · entry-orphan-badge ·
 *   entry-owner-badge · entry-detail-mine-badge · entry-collection-select ·
 *   entry-file-chip · entry-add-file · action-claim · action-transfer ·
 *   keybook-exposure-line (managers; "This key might be exposed to
 *   Organization owner and N others"; its exposure-view opens the Exposure
 *   dialog, keybook-exposure-dialog, whose exposure-row carries
 *   data-principal-type role|team|email|app|tenant — no role / team members) ·
 *   buttons "New Entry" / "Save" / "Reveal" / "Access" (exact). The
 *   modal's close button is "Close details" — the exact name "Close"
 *   belongs to the access/collections dialogs. The detail modal has a
 *   scrim, so share.spec helpers press Escape before re-clicking rows
 *   or header buttons (updated in lockstep with this redesign).
 */
import { useState, useCallback, useEffect, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, useTheme, getThemeClass, BreadcrumbPath, FileIcon, Tooltip, avatarBg, getInitials, FileContextMenu, type FileMenuItem } from '@papan/core-ui/ui'
import { useRealtime } from '@papan/realtime/client'
import { erasedOwnerEmail } from '@papan/types'
import { offersKeybookAdminView } from './admin-view.js'
import { connectorIcon } from './key-icons.js'
import { readKeySort, writeKeySort, sortKeys, type KeySort } from './key-sort.js'
import { KeySortMenu } from './KeySortMenu.js'
import { readKeyCardPlaces, slideKeyCardsFrom } from './key-reorder-animation.js'
import { credentialTypeLabel, getDisplayFieldsForType, STATIC_SCHEMA_TYPES } from './credential-type-ui.js'
import {
  Plus, Key, Eye, EyeOff, Pencil, Trash2, BookKey, RefreshCw, Users, AtSign,
  Tag, AlertCircle, ArrowRightLeft, Hand, FolderInput, X, Copy, Check,
  ExternalLink, Building2, Library, History, Loader2, User,
  Folder, Share2, ArrowLeft, ShieldCheck,
  Lock, Bot, MoreHorizontal, Search, Settings, ChevronDown,
} from 'lucide-react'
import { getApiBase } from '@papan/api-client'
import { useKeybook } from '../client/hooks.js'
import { keybookApi, type KeybookExposure } from '../client/api.js'
import { EntryCreateDialog } from './EntryCreateDialog.js'
import { KeyAccessDialog } from './KeyAccessDialog.js'
import { KeyExposureDialog } from './KeyExposureDialog.js'
import { ExposureHeadline, exposureHeadlineParts } from './ExposureHeadline.js'
import { KeyDeleteNotice, loadDeleteExposure } from './KeyRevokeNotice.js'
import { canSeeKeybookAccess } from '../core/access-visibility.js'
import { TransferOwnerDialog } from './TransferOwnerDialog.js'
import type { EntryMetadata, KeybookEntryData } from '../core/types.js'
import { useBrand } from '@papan/brand/react'

interface KeybookViewProps {
  userId?: string
  /** The org-email identity the caller is currently acting as.
   *  Drives ownership of created entries (`(tenantId, email)` is the
   *  scope) and "is this mine" badges in the list. */
  userEmail?: string
  /** Caller's role in the active tenant — drives admin-only actions
   *  like Claim / Reassign and the orphan badge visibility. */
  userRole?: string
  tenantId: string
  isOrg: boolean
  /** False while the desktop shell keeps this deck mounted but hidden
   *  (`display: none`): the portaled detail and dialogs live on <body>, so
   *  they are closed rather than left floating over another deck. Absent =
   *  always shown (mobile mounts a real route). */
  deckActive?: boolean
  FilePickerDialog?: React.ComponentType<{ tenantId: string; title?: string; onSelect: (id: string, isFolder: boolean) => void; onClose: () => void }>
}

/** Coarse "X ago" formatter for usage badges. Shorter than dates +
 *  contextually clear: if it's stale (>30d) we say "long ago" rather
 *  than a misleading "32d ago" — the precise day doesn't matter at
 *  that point, only that the user can't trust the cred is fresh. */
function formatAgo(iso: string | null | undefined): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return null
  const diff = Date.now() - t
  if (diff < 60_000) return 'just now'
  const min = Math.floor(diff / 60_000)
  if (min < 60) return `${min}m ago`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return 'long ago'
}

/** Fobs share ONE cap color — the app's purple — with a subtly bluer
 *  cap for team/role-owned keys so org-level keys read at a glance.
 *  (A per-label rainbow made the rack loud and meaningless; with one
 *  color the rack reads by connector icon + name, and TINT means
 *  ownership. Gold stays the hardware accent: pins, glyphs, rails.) */
function fobHue(entry: Pick<EntryMetadata, 'ownerType'>): number {
  return entry.ownerType === 'team' || entry.ownerType === 'role' ? 222 : 258
}

/** Whether the intro's protection points are expanded — per browser, never
 *  per account: it is a reading aid, not a setting. Unset = collapsed (owner,
 *  2026-11-03): the one-line lead shows; the points open on request. */
const INTRO_OPEN_KEY = 'keybook.introOpen'

/** A section rail: the engraved label plate and ONE glass hairline — a 1px
 *  line in the hero's pill-border token (the same hairline the drive's
 *  tiles carry), not a carved groove that out-weighs the glass above it. */
function Rail({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <span className="kbg-rail-plate">{children}</span>
      <span aria-hidden className="flex-1 h-px" style={{ background: 'var(--ws-dpill-border)' }} />
    </div>
  )
}

/* The type shapes, labels and default masking: ./credential-type-ui (derived from the one type list, #1324 I1). */

/* Connector identity (a key's icon by what it connects to) lives in ./key-icons, shared with the app settings' key
 * picker. The per-key hue stays label-derived (identity), the icon is semantic. */

/** Audit event type → i18n key suffix under activity.events.* — falls
 *  back to the raw event type for anything the map doesn't know. */
const AUDIT_EVENT_KEYS: Record<string, string> = {
  created: 'created',
  value_updated: 'valueUpdated',
  note_updated: 'noteUpdated',
  metadata_updated: 'metadataUpdated',
  collection_changed: 'collectionChanged',
  deleted: 'deleted',
  access_granted: 'accessGranted',
  access_revoked: 'accessRevoked',
  file_bound: 'fileBound',
  file_unbound: 'fileUnbound',
  ownership_transferred: 'ownershipTransferred',
  ownership_claimed: 'ownershipClaimed',
  token_rotation: 'tokenRotation',
  app_access_granted: 'appAccessGranted',
  app_access_revoked: 'appAccessRevoked',
}

/** LED health for a key fob:
 *    warn — orphaned owner, or the most recent use of any binding failed
 *    ok   — used successfully at some point (and nothing failing)
 *    off  — never used */
function cardLedState(entry: EntryMetadata): 'ok' | 'warn' | 'off' {
  if (entry.ownerType !== 'role' && !entry.ownerActive) return 'warn'
  if (entry.files?.some(f => f.lastFailureReason)) return 'warn'
  if (entry.files?.some(f => f.lastSuccessAt) || entry.lastUsedAt) return 'ok'
  return 'off'
}

function groupByCollection(entries: EntryMetadata[]): Map<string, EntryMetadata[]> {
  const out = new Map<string, EntryMetadata[]>()
  for (const e of entries) {
    if (!e.collectionId) continue
    const bucket = out.get(e.collectionId) ?? []
    bucket.push(e)
    out.set(e.collectionId, bucket)
  }
  return out
}

export function KeybookView({ userEmail, userRole, tenantId, isOrg, FilePickerDialog, deckActive = true }: KeybookViewProps) {
  /** An owner email as shown — a deleted account's reads "x@y (deleted account)". */
  const ownerText = (ownerId: string) => {
    const gone = erasedOwnerEmail(ownerId)
    return gone ? t('ownerDeletedAccount', { email: gone }) : ownerId
  }
  const callerEmailLower = userEmail?.toLowerCase()
  const { t } = useTranslation('keybook')
  // The modal + dialogs PORTAL to <body> — an in-tree `fixed` overlay
  // loses to the taskbar (an ancestor makes a lower stacking context),
  // so portaled surfaces re-apply the theme class themselves.
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  const confirm = useConfirm()
  const { onKeybookEvent } = useRealtime()

  // Admins default to "mine" — tenant-wide is opt-in via the toggle.
  // Non-admins always get "mine"; the server ignores any other value.
  // "Show as admin" — off by default, as on the Projects / PDApps pages. Offered
  // when the server marks keys this person reaches only through their role (#1102 item 6).
  const [showAsAdmin, setShowAsAdmin] = useState(false)
  // The intro — what the keybook is for and how keys are protected, in plain
  // words: one line always, its four points collapsible, remembered per
  // browser (collapsed until opened).
  const [introOpen, setIntroOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(INTRO_OPEN_KEY) === '1' } catch { return false }
  })
  const toggleIntro = () => setIntroOpen(open => {
    try { localStorage.setItem(INTRO_OPEN_KEY, open ? '0' : '1') } catch { /* blocked storage: collapsed again next load */ }
    return !open
  })
  const keybook = useKeybook(tenantId, userEmail, userRole)
  // What a caller may DO comes from the server's answers — never their role:
  // whether they manage the keybook root (root dialog, collection create) and
  // each key's resolved permission. A lowered admin gets no controls that would
  // 403; a non-admin manager gets theirs.
  const managesRoot = keybook.managesRoot

  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null)
  // The selection NOW — an async handler (an edit save) reads this when it
  // settles, never the value its render captured.
  const selectedEntryIdRef = useRef<string | null>(null)
  selectedEntryIdRef.current = selectedEntryId
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  // Read by the GitHub install listener: only the tab whose New Entry dialog is open
  // started that sign-in, so only it jumps to the new key.
  const showCreateDialogRef = useRef(false)
  showCreateDialogRef.current = showCreateDialog
  /** The open editor: the value and the `version` it was read at (the save
   *  is refused if the key changed since); `changed` = the server said so. */
  const [editingEntry, setEditingEntry] = useState<{ id: string; data: KeybookEntryData; version: number; meta: EntryMetadata; changed?: boolean } | null>(null)
  /** Decrypted data for the selected entry — only loaded on user
   *  action (reveal or copy). Until then, field shapes come from the
   *  credentialType schema and values stay masked. Cleared on
   *  selection change so we never leak across entries. */
  // Decrypted secrets and the "revealed" flag are BOUND to the key they belong
  // to: rendered only while that key is the one selected. Clearing them in an
  // effect after a selection change left one committed frame (or more) showing
  // key A's plaintext under key B's labels.
  const [decryptedState, setDecryptedState] = useState<{ entryId: string; data: KeybookEntryData } | null>(null)
  const decryptedData = decryptedState && decryptedState.entryId === selectedEntryId ? decryptedState.data : null
  const setDecryptedData = (v: null) => setDecryptedState(v)
  const [decryptError, setDecryptError] = useState<string | null>(null)
  /** Global reveal toggle — single click flips all fields at once
   *  (avoiding per-field click fatigue). Copy buttons appear inside
   *  the field box when revealAll is true. */
  const [revealFor, setRevealFor] = useState<string | null>(null)
  const revealAll = revealFor !== null && revealFor === selectedEntryId && decryptedData !== null
  /** Callers only ever HIDE through this; revealing goes through setRevealFor(id). */
  const setRevealAll = (_hide: false) => setRevealFor(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [copiedKeyId, setCopiedKeyId] = useState(false)
  /** Single in-flight decryption promise so back-to-back reveal +
   *  copy don't fire two requests. */
  const decryptInFlight = useRef<Promise<KeybookEntryData> | null>(null)
  /** Bumped whenever cached secrets are invalidated (another key selected, the
   *  same key updated / re-synced / edited) — a decrypt that resolves after a
   *  bump is stale and must not land (it could put another key's or the
   *  pre-update secret on screen). The state twin re-runs the auto-decrypt
   *  effect, which a no-op `setDecryptedData(null)` would not. */
  const decryptGen = useRef(0)
  /** Bumped only when another key is selected (decryptGen also bumps on a
   *  same-key update, which must not drop that key's activity log). */
  const auditGen = useRef(0)
  const [decryptEpoch, setDecryptEpoch] = useState(0)
  /** True while the delete confirm is open, so its Escape doesn't also
   *  close the detail modal underneath. */
  const confirmingRef = useRef(false)
  /** The detail modal's dialog element, and what had focus before it opened
   *  (focus moves into the modal on open and back on close). */
  const detailRef = useRef<HTMLDivElement | null>(null)
  const focusBeforeDetail = useRef<HTMLElement | null>(null)
  /** The access dialog for a key — `readOnly` (D27) for Write / the capability. */
  const [sharingEntry, setSharingEntry] = useState<{ id: string; label: string; credentialType: string; readOnly?: boolean } | null>(null)
  /** Collection being shared from a folder tile / folder header. */
  const [sharingCollection, setSharingCollection] = useState<{ id: string; name: string; readOnly?: boolean } | null>(null)
  const [showRootAccess, setShowRootAccess] = useState(false)
  // Collections the caller can reach — drives the folder strip AND the
  // per-entry "move to collection" selector. Reloaded after every
  // collection change made here (create / rename / delete).
  const [collectionOptions, setCollectionOptions] = useState<Array<{ id: string; name: string; permission: string; hiddenWithoutAdminView?: boolean }>>([])
  // #1102 item 6: "Show as admin" is offered from the SERVER's answer, never the role (see `offersKeybookAdminView`).
  const adminView = offersKeybookAdminView(isOrg, keybook.entries, collectionOptions)
  // Tied to the view (tenant, identity, role): cleared on a switch, a late
  // answer for an earlier view dropped, reloaded on keybook realtime events
  // (a collection newly shared with me) as well as after a change made here.
  const [collectionsTick, setCollectionsTick] = useState(0)
  /** manage_keybook_permission as the collections list answered it (resolved once). */
  const [collectionsAccessCapability, setCollectionsAccessCapability] = useState<'none' | 'read' | 'write'>('none')
  useEffect(() => {
    setCollectionOptions([])
    setCollectionsAccessCapability('none')
  }, [tenantId, userEmail, userRole])
  useEffect(() => {
    if (!tenantId) return
    let live = true
    keybookApi.listCollections(tenantId)
      .then(r => { if (live) { setCollectionOptions(r.collections); setCollectionsAccessCapability(r.accessCapability ?? 'none') } })
      .catch(() => {})
    return () => { live = false }
  }, [tenantId, userEmail, userRole, collectionsTick])
  useEffect(() => {
    if (!tenantId) return
    return onKeybookEvent(event => {
      if (event.tenantId === tenantId) setCollectionsTick(n => n + 1)
    })
  }, [tenantId, onKeybookEvent])
  /** Gallery filter: 'all' | a collection id (= that folder is open). */
  const [collectionFilter, setCollectionFilter] = useState<string>('all')
  const handleSetCollection = useCallback(async (entryId: string, collectionId: string | null) => {
    try { await keybookApi.setEntryCollection(entryId, collectionId); keybook.refreshEntries() }
    catch (err: any) { toast.error(err?.message || 'Failed to move to collection') }
  }, [keybook, toast])

  // ── Collection actions live ON the collection (no manager dialog) ──
  // Add = the ghost tile at the end of the strip (keybook root managers —
  // the server's `managesRoot`, re-checked server-side). Share / Rename /
  // Delete = the tile's ⋯ / right-click glass menu and the open folder's
  // header, shown only when THAT collection resolves `manage`.
  const [addingCollection, setAddingCollection] = useState(false)
  const [newCollectionName, setNewCollectionName] = useState('')
  const [collectionBusy, setCollectionBusy] = useState(false)
  /** Inline rename — on the tile, or the open folder's title. */
  const [renamingCollection, setRenamingCollection] = useState<{ id: string; value: string } | null>(null)
  /** The tile menu: which collection, and where it opened. */
  const [collectionMenu, setCollectionMenu] = useState<{ id: string; name: string; permission: string; x: number; y: number } | null>(null)
  const collectionMenuRef = useRef<HTMLDivElement>(null)
  /** The ⋯ button that opened the menu — focus goes back to it on close (keyboard users). */
  const collectionMenuTrigger = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (collectionMenu) {
      // Opened from the ⋯ (keyboard or mouse): focus its first item.
      if (collectionMenuTrigger.current) requestAnimationFrame(() => collectionMenuRef.current?.querySelector<HTMLElement>('button:not([disabled])')?.focus())
      return
    }
    const trigger = collectionMenuTrigger.current
    collectionMenuTrigger.current = null
    trigger?.focus()
  }, [collectionMenu])
  useEffect(() => {
    if (!collectionMenu) return
    const onDoc = (e: MouseEvent) => {
      if (collectionMenuRef.current?.contains(e.target as Node)) return
      setCollectionMenu(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCollectionMenu(null) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [collectionMenu])
  const createCollection = async () => {
    const name = newCollectionName.trim()
    if (!name || collectionBusy) return
    setCollectionBusy(true)
    try {
      await keybookApi.createCollection(tenantId, name)
      setNewCollectionName('')
      setAddingCollection(false)
      setCollectionsTick(n => n + 1)
    } catch (err: any) { toast.error(err?.message || t('collections.createFailed')) }
    finally { setCollectionBusy(false) }
  }
  const commitRenameCollection = async () => {
    if (!renamingCollection) return
    const { id, value } = renamingCollection
    setRenamingCollection(null)
    const name = value.trim()
    const current = collectionOptions.find(c => c.id === id)
    if (!name || !current || name === current.name) return
    try { await keybookApi.renameCollection(id, name); setCollectionsTick(n => n + 1) }
    catch (err: any) { toast.error(err?.message || t('collections.renameFailed')) }
  }
  const deleteCollection = async (coll: { id: string; name: string }) => {
    // Every key filed there the viewer can read — hidden by the switch or not.
    const count = keybook.entries.filter(e => e.collectionId === coll.id).length
    const ok = await confirm({
      title: t('collections.deleteTitle'),
      message: t('collections.deleteMessage', { name: coll.name, count }),
      confirmText: t('delete'),
      type: 'danger',
    })
    if (!ok) return
    try {
      await keybookApi.deleteCollection(coll.id)
      if (collectionFilter === coll.id) setCollectionFilter('all')
      setCollectionsTick(n => n + 1)
      // The keys it held are unfiled now — their case labels go.
      keybook.refreshEntries().catch(() => {})
    } catch (err: any) { toast.error(err?.message || t('collections.deleteFailed')) }
  }
  /** Manage: Manage access (the editable access list), Rename, Delete. Write — or Read with
   *  manage_keybook_permission — gets Access, the read-only list (D27). */
  const collectionMenuItems = (c: { id: string; name: string; permission: string }): FileMenuItem[] => c.permission !== 'manage' ? [
    { kind: 'item', icon: Users, label: t('access.viewItem'), testId: 'collection-access',
      onClick: () => { setCollectionMenu(null); setSharingCollection({ id: c.id, name: c.name, readOnly: true }) } },
  ] : [
    // Not in a personal keybook: it has no one to share with (as keys there).
    ...(isOrg ? [{ kind: 'item' as const, icon: Users, label: t('access.manageTitle'), testId: 'collection-share',
      onClick: () => { setCollectionMenu(null); setSharingCollection({ id: c.id, name: c.name }) } }] : []),
    { kind: 'item', icon: Pencil, label: t('collections.rename'), testId: 'collection-rename',
      onClick: () => { setCollectionMenu(null); setRenamingCollection({ id: c.id, value: c.name }) } },
    { kind: 'separator' },
    { kind: 'item', icon: Trash2, label: t('delete'), danger: true, testId: 'collection-delete',
      onClick: () => { setCollectionMenu(null); void deleteCollection(c) } },
  ]
  /** The inline rename field — the tile's and the folder header's are one control. */
  const renderCollectionRenameInput = (className: string) => renamingCollection && (
    <input
      autoFocus
      value={renamingCollection.value}
      onChange={e => setRenamingCollection(r => r && { ...r, value: e.target.value })}
      onBlur={() => void commitRenameCollection()}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Enter') void commitRenameCollection()
        else if (e.key === 'Escape') setRenamingCollection(null)
      }}
      aria-label={t('collections.rename')}
      data-testid="collection-rename-input"
      className={`themed-input rounded-md text-sm ${className}`}
    />
  )
  const [transferring, setTransferring] = useState<{
    id: string
    label: string
    currentOwner: { type: 'email' | 'team' | 'role'; id: string }
    mode: 'transfer' | 'claim' | 'reassign'
  } | null>(null)
  /** Entry id that the user is adding a file binding to. Drives the
   *  FilePickerDialog rendered at the bottom — null means closed. */
  const [addingFileFor, setAddingFileFor] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  /** Gallery order, inside each section — Name by default; remembered per browser. */
  const [keySort, setKeySort] = useState<KeySort>(readKeySort)
  // A sort change slides the cards to their new places: read where they are now,
  // then — once the new order is laid out — animate from there.
  const reorderFrom = useRef<Map<string, DOMRect> | null>(null)
  const changeKeySort = (next: KeySort) => {
    if (next !== keySort) reorderFrom.current = readKeyCardPlaces()
    setKeySort(next)
    writeKeySort(next)
  }
  useLayoutEffect(() => {
    const from = reorderFrom.current
    reorderFrom.current = null
    if (from) slideKeyCardsFrom(from)
  }, [keySort])
  /** A key just created: scrolled into view and focused once its fob is on the rack, then marked briefly. */
  const [focusEntryId, setFocusEntryId] = useState<string | null>(null)
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null)
  /** Inline rename state for the entry title. Null = not editing. */
  const [renamingTitle, setRenamingTitle] = useState<string | null>(null)
  /** Inline edit state for the caller's personal nickname. */
  const [editingNickname, setEditingNickname] = useState<string | null>(null)
  /** Activity timeline — lazily fetched on first expand per entry. */
  const [activityOpen, setActivityOpen] = useState(false)
  const [auditEvents, setAuditEvents] = useState<Array<{
    id: string; eventType: string; newValue: any; oldValue: any; eventAt: string
  }> | null>(null)
  const [auditLoading, setAuditLoading] = useState(false)

  const selectedEntry = keybook.entries.find(e => e.id === selectedEntryId)
  // Manage gate for the selected entry's inline detail controls (move-to-collection,
  // activity log) — the key's resolved permission from the list. The server
  // re-checks on every mutation; this only governs what's shown.
  const canManageSelected = !!selectedEntry && selectedEntry.permission === 'manage'
  // Edit the value, bind / unbind files, refresh a GitHub key: write or manage.
  const canWriteSelected = !!selectedEntry && (selectedEntry.permission === 'write' || selectedEntry.permission === 'manage')

  // Exposure (plan 2026-10-16): who might have seen the selected key's current
  // value — shown to whoever may see its access list (D26: Manage or Write on the
  // key, or Read with manage_keybook_permission ≥ read — keybookAccessView), in an org.
  // Refetched when the key changes (a new value starts a new period) and when
  // the access dialog closes (a grant can add to it).
  const showExposure = !!isOrg && !!selectedEntry && canSeeKeybookAccess(selectedEntry.permission, keybook.accessCapability)
  const [exposure, setExposure] = useState<{ entryId: string; data: KeybookExposure } | null>(null)
  const [exposureTick, setExposureTick] = useState(0)
  /** The Exposure dialog, opened from the exposure line. */
  const [exposureOpen, setExposureOpen] = useState<{ id: string; label: string } | null>(null)
  useEffect(() => {
    if (!showExposure || !selectedEntryId) return
    let alive = true
    keybookApi.getEntryExposure(selectedEntryId)
      .then(data => { if (alive) setExposure({ entryId: selectedEntryId, data }) })
      .catch(() => { if (alive) setExposure(null) })
    return () => { alive = false }
  }, [showExposure, selectedEntryId, selectedEntry?.updatedAt, exposureTick])

  // Resolve file IDs to paths for display
  /** Per-binding cache of resolved file metadata. The chip in the
   *  modal needs the name + path + folder/file flag for icon
   *  selection; the fob's binding hint just uses the name. */
  type FileMeta = { path: string; name: string; isFolder: boolean }
  const filePathCache = useRef<Record<string, FileMeta>>({})
  const [filePaths, setFilePaths] = useState<Record<string, FileMeta>>({})

  // Map teamId → team name for the owner badges under "Show as admin". Only
  // fetched when that's on, so members never pay the round-trip.
  const [teamNames, setTeamNames] = useState<Record<string, string>>({})
  useEffect(() => {
    if (!adminView || !showAsAdmin) return
    let cancelled = false
    ;(async () => {
      try {
        const { request } = await import('@papan/api-client')
        const { teams } = await request<{ teams: Array<{ id: string; name: string }> }>(
          `/api/orgs/${tenantId}/teams`,
        )
        if (cancelled) return
        const map: Record<string, string> = {}
        for (const t of teams) map[t.id] = t.name
        setTeamNames(map)
      } catch { /* best-effort — falls back to teamId display */ }
    })()
    return () => { cancelled = true }
  }, [adminView, showAsAdmin, tenantId])

  useEffect(() => {
    // Pull every bound file across every entry — the chip list and
    // breadcrumb both index into filePathCache by file id.
    const fileIds = keybook.entries
      .flatMap(e => e.fileIds ?? [])
      .filter((id): id is string => !!id && !filePathCache.current[id])

    if (fileIds.length === 0) return

    Promise.all(
      [...new Set(fileIds)].map(async (id) => {
        try {
          const { filesApi } = await import('@papan/files/client')
          const info: any = await filesApi.getFileInfo(id)
          filePathCache.current[id] = {
            path: info.path || info.name || id,
            name: info.name || id,
            isFolder: !!info.isFolder,
          }
        } catch {
          filePathCache.current[id] = { path: id, name: id, isFolder: false }
        }
      })
    ).then(() => setFilePaths({ ...filePathCache.current }))
  }, [keybook.entries])

  // Keys whose edit save is still in flight. The dialog can't be dismissed
  // mid-save, but leaving the deck unmounts it; a reopened editor must not
  // start a second value write that the first (no expected version) could
  // overwrite by landing last.
  const savesInFlight = useRef(new Set<string>())

  const handleEdit = useCallback(async (entry: EntryMetadata) => {
    if (savesInFlight.current.has(entry.id)) {
      toast.info(t('actions.saveInFlight'))
      return
    }
    // auditGen bumps when the open key changes or closes (incl. a deck or view
    // switch): then the dialog must not open. decryptGen also bumps when the
    // SAME key was updated meanwhile — then read it again (once) rather than
    // open the editor on pre-update data or swallow the click.
    const openGen = auditGen.current
    try {
      let gen = decryptGen.current
      let read = await keybook.readEntryForEdit(entry.id)
      // deckActiveRef too: the deck can be left (ref already false) before the
      // leave-effect bumps auditGen — never open the editor on a hidden deck.
      if (auditGen.current !== openGen || !deckActiveRef.current) return
      if (decryptGen.current !== gen) {
        gen = decryptGen.current
        read = await keybook.readEntryForEdit(entry.id)
        if (auditGen.current !== openGen || decryptGen.current !== gen || !deckActiveRef.current) return
      }
      setEditingEntry({ id: entry.id, data: read.data, version: read.version, meta: entry })
    } catch (err: any) {
      toast.error(err.message || 'Failed to read entry')
    }
  }, [keybook, toast, t])

  /** Clear cached secrets + modal-local state when the user picks a
   *  different key. No auto-decrypt for static-schema types — we only
   *  fetch on Reveal click so secrets never sit in browser memory
   *  unrequested. */
  useEffect(() => {
    setDecryptedData(null)
    setDecryptError(null)
    setRevealAll(false)
    setRenamingTitle(null)
    setEditingNickname(null)
    setActivityOpen(false)
    setAuditEvents(null)
    setCopiedKeyId(false)
    decryptInFlight.current = null
    decryptGen.current++
    auditGen.current++
    setAuditLoading(false)
  }, [selectedEntryId])

  /** Focus moves into the detail modal when it opens and returns to what had
   *  it (the key card) when it closes. */
  const detailOpen = !!selectedEntryId
  useEffect(() => {
    if (!detailOpen) return
    const openedId = selectedEntryId
    focusBeforeDetail.current = document.activeElement as HTMLElement | null
    detailRef.current?.focus()
    return () => {
      // The element that had focus, or — if it was re-rendered away — the
      // same key's card.
      const el = focusBeforeDetail.current
      if (el && document.contains(el)) el.focus()
      else if (openedId) document.querySelector<HTMLElement>(`[data-entry-id="${CSS.escape(openedId)}"]`)?.focus()
      focusBeforeDetail.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailOpen])

  /** The held list belongs to this view (tenant, identity, role);
   *  the "Loading..." placeholder shows only until it does. */
  const hasLoaded = keybook.hasLoaded

  /** The open key left the list (deleted by someone, access revoked,
   *  another tenant / identity): close it, so its modal doesn't reappear
   *  on its own if the key comes back and its listeners don't linger. */
  useEffect(() => {
    if (!hasLoaded || keybook.isLoading || !selectedEntryId) return
    if (!keybook.entries.some(e => e.id === selectedEntryId)) setSelectedEntryId(null)
  }, [hasLoaded, keybook.isLoading, keybook.entries, selectedEntryId])

  /** A tenant / identity / role switch starts the view fresh — "Show as
   *  admin", a collection filter or an open key belong to the previous one. */
  const viewKey = `${tenantId}|${userRole ?? ''}|${userEmail ?? ''}`
  const prevViewKey = useRef(viewKey)
  useEffect(() => {
    if (prevViewKey.current === viewKey) return
    prevViewKey.current = viewKey
    setShowAsAdmin(false)
    setCollectionFilter('all')
    setSearchQuery('')
    setSelectedEntryId(null)
    // Every dialog too: one kept (hidden) while the user was on another deck
    // would otherwise act on the NEW tenant with the old one's entry / draft
    // (a credential typed for org A created in org B). Losing a draft is right
    // when the org or identity changed.
    setShowCreateDialog(false)
    setEditingEntry(null)
    setSharingEntry(null)
    setSharingCollection(null)
    setCollectionMenu(null)
    setRenamingCollection(null)
    setAddingCollection(false)
    setTransferring(null)
    setAddingFileFor(null)
  }, [viewKey])

  const deckActiveRef = useRef(deckActive)
  deckActiveRef.current = deckActive
  /** Coming back to the deck refreshes the list and collections: events
   *  missed while hidden (or our own from another tab, which the hook's
   *  realtime handler skips) would otherwise leave them stale (keep-alive
   *  decks don't remount). */
  const wasDeckActive = useRef(deckActive)
  useEffect(() => {
    if (deckActive && !wasDeckActive.current) {
      keybook.refreshEntries().catch(() => {})
      setCollectionsTick(n => n + 1)
    }
    wasDeckActive.current = deckActive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckActive])
  /** The deck hides with display:none, but the detail and dialogs are
   *  portaled to <body>. Leaving the deck closes the read-only detail and
   *  the Edit dialog, and HIDES the other dialogs (`.kb-deck-portal` under `body[data-keybook-hidden]`)
   *  — their state is kept, so a half-typed token or password is still there
   *  when the user comes back. */
  // The EDIT dialog is closed rather than hidden (owner decision): it holds a
  // key's DECRYPTED values from the server, which must not sit in the page
  // while the user is on another deck — reopening it costs nothing. The CREATE
  // dialog keeps its draft: only what the user typed.
  useEffect(() => {
    if (deckActive) return
    // Bumped here, not only by the selection effect, so an Edit decrypt that
    // resolves before React flushes the closed selection can't reopen it.
    auditGen.current++
    setSelectedEntryId(null)
    setEditingEntry(null)
    document.body.dataset.keybookHidden = '1'
    return () => { delete document.body.dataset.keybookHidden }
  }, [deckActive])

  /** Any invalidation of the cached secrets (decryptEpoch) also re-masks
   *  them, so "Hide" never shows over masked values. */
  useEffect(() => { setRevealAll(false) }, [decryptEpoch])

  /** Escape closes the detail modal — but only when it's the topmost
   *  surface (child dialogs and the inline editors own their Esc). */
  useEffect(() => {
    if (!selectedEntryId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Tab') return
      // A child dialog / the confirm owns the keyboard while it is open.
      if (showCreateDialog || editingEntry || sharingEntry || sharingCollection || transferring || addingFileFor) return
      if (confirmingRef.current) return
      if (e.key === 'Tab') {
        // Keep Tab inside the modal (aria-modal) — wrap at either end.
        const root = detailRef.current
        if (!root) return
        const items = Array.from(root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )).filter(el => el.offsetParent !== null)
        if (items.length === 0) { e.preventDefault(); root.focus(); return }
        const first = items[0], last = items[items.length - 1]
        const active = document.activeElement
        if (e.shiftKey && (active === first || active === root || !root.contains(active))) { e.preventDefault(); last.focus() }
        else if (!e.shiftKey && (active === last || !root.contains(active))) { e.preventDefault(); first.focus() }
        return
      }
      if (renamingTitle !== null || editingNickname !== null) return
      setSelectedEntryId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedEntryId, showCreateDialog, editingEntry, sharingEntry, sharingCollection, transferring, addingFileFor, renamingTitle, editingNickname])

  /** For 'custom' and legacy/unknown types we don't know the field
   *  names without decrypting, so trigger ensureDecrypted on
   *  selection. Values still stay masked until Reveal — only the
   *  field-name shape leaks to the client at this stage. */
  useEffect(() => {
    if (!selectedEntryId || !selectedEntry) return
    if (STATIC_SCHEMA_TYPES.has(selectedEntry.credentialType)) return
    if (decryptedData) return
    void ensureDecrypted(selectedEntryId)
    // ensureDecrypted is intentionally not in deps — it's stable
    // enough for this effect (depends on decryptedData + keybook),
    // and we explicitly gate on `decryptedData` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntryId, selectedEntry?.credentialType, decryptedData, decryptEpoch])

  /** Decrypt on demand and cache. */
  const ensureDecrypted = useCallback(async (entryId: string): Promise<KeybookEntryData | null> => {
    if (decryptedData) return decryptedData
    // A caller joining a decrypt already in flight is held to the SAME
    // generation check — else a Reveal pressed mid-flight would act on
    // whatever key / version is shown when it resolves.
    const gen = decryptGen.current
    const joined = decryptInFlight.current
    const p = joined ?? keybook.decryptEntry(entryId)
    if (!joined) decryptInFlight.current = p
    try {
      const data = await p
      if (decryptGen.current !== gen) return null
      if (!joined) {
        setDecryptedState({ entryId, data })
        setDecryptError(null) // a retry after an invalidation clears the old failure
      }
      return data
    } catch (err: any) {
      if (!joined && decryptGen.current === gen) setDecryptError(err.message || 'Failed to decrypt')
      return null
    } finally {
      if (!joined && decryptInFlight.current === p) decryptInFlight.current = null
    }
  }, [decryptedData, keybook])

  const handleToggleReveal = useCallback(async (entryId: string) => {
    if (revealAll) {
      setRevealAll(false)
      return
    }
    const data = await ensureDecrypted(entryId)
    if (data) setRevealFor(entryId)
  }, [revealAll, ensureDecrypted])

  const handleCopyField = useCallback(async (fieldName: string) => {
    if (!decryptedData) return
    const value = decryptedData.fields?.[fieldName] ?? ''
    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(fieldName)
      toast.success(t('actions.copied'))
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }, [decryptedData, toast, t])

  const handleCopyKeyId = useCallback(async (entryId: string) => {
    try {
      await navigator.clipboard.writeText(entryId)
      setCopiedKeyId(true)
      toast.success(t('actions.copied'))
      setTimeout(() => setCopiedKeyId(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }, [toast, t])

  /** Persist a rename from the inline pencil editor. Trims input,
   *  drops no-op renames, and refreshes the list so the new label
   *  shows up in the gallery. */
  const handleSaveRename = useCallback(async (entryId: string, currentLabel: string) => {
    const next = (renamingTitle ?? '').trim()
    if (!next) { setRenamingTitle(null); return }
    if (next === currentLabel) { setRenamingTitle(null); return }
    try {
      const { keybookApi } = await import('../client/api.js')
      await keybookApi.updateEntryMetadata(entryId, { sourceLabel: next })
      setRenamingTitle(null)
      keybook.refreshEntries()
    } catch (err: any) {
      toast.error(err.message || 'Failed to rename')
    }
  }, [renamingTitle, keybook, toast])

  /** Persist the caller's personal nickname (per-member custom label —
   *  overrides the shared label just for this member). Empty = clear. */
  const handleSaveNickname = useCallback(async (entryId: string) => {
    const next = (editingNickname ?? '').trim()
    try {
      await keybookApi.setCustomLabel(entryId, next || null)
      setEditingNickname(null)
      keybook.refreshEntries()
    } catch (err: any) {
      toast.error(err.message || 'Failed to set nickname')
    }
  }, [editingNickname, keybook, toast])

  const deleteOpeningRef = useRef(false)
  const handleDelete = useCallback(async (entryId: string, _label: string) => {
    // One delete confirm at a time — the exposure read below would otherwise let
    // a double click open two.
    if (deleteOpeningRef.current) return
    const gen = auditGen.current // bumped when the open key changes / closes
    const doomed = keybook.entries.find(e => e.id === entryId)
    // Who might have it is read BEFORE the confirm opens, so the line is there
    // from the first frame — and FRESH: the detail's copy can miss people who
    // opened the key since (an agent run records them without touching the key),
    // so it is only the fallback when the read fails.
    deleteOpeningRef.current = true
    let doomedExposure: KeybookExposure | null
    try {
      doomedExposure = (await loadDeleteExposure(entryId, !!isOrg))
        ?? (exposure?.entryId === entryId ? exposure.data : null)
    } finally {
      deleteOpeningRef.current = false
    }
    if (auditGen.current !== gen || !deckActiveRef.current) return
    confirmingRef.current = true
    let confirmed: boolean
    try {
      confirmed = await confirm({
        title: t('actions.deleteTitle'),
        // Deleting removes only Papan's copy: who might have it, and how to
        // revoke it where it was issued (plan 2026-10-17_KEYBOOK_REVOKE).
        message: <KeyDeleteNotice credentialType={doomed?.credentialType} provider={doomed?.provider} exposure={doomedExposure} />,
        confirmText: t('delete'),
        type: 'danger',
      })
    } finally {
      // Cleared on the next task, after any keydown still being dispatched.
      setTimeout(() => { confirmingRef.current = false }, 0)
    }
    if (!confirmed) return
    // The confirm is app-global and outlives the detail: a confirm answered
    // after the key closed (deck left with Back, view switch) deletes nothing.
    if (auditGen.current !== gen || !deckActiveRef.current) return
    try {
      await keybook.deleteEntry(entryId)
      if (selectedEntryId === entryId) setSelectedEntryId(null)
      toast.success(t('actions.deleted'))
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete')
    }
  }, [keybook, confirm, t, toast, selectedEntryId, isOrg, exposure])

  /** Lazy-load the audit trail the first time the section opens. */
  const handleToggleActivity = useCallback(async (entryId: string) => {
    const next = !activityOpen
    setActivityOpen(next)
    if (!next || auditEvents) return
    setAuditLoading(true)
    const gen = auditGen.current
    try {
      const { events } = await keybookApi.getEntryAudit(entryId)
      if (auditGen.current !== gen) return // another key is open now
      setAuditEvents(events)
    } catch (err: any) {
      if (auditGen.current !== gen) return
      toast.error(err?.message || 'Failed to load activity')
      setActivityOpen(false)
    } finally {
      if (auditGen.current === gen) setAuditLoading(false)
    }
  }, [activityOpen, auditEvents, toast])

  // Realtime: when the server fires keybook:entry_updated for the
  // currently-selected entry, invalidate decryptedData so the modal
  // re-fetches the new metadata. useKeybook's own subscription
  // already refreshes the entry list — this one just covers the
  // modal's cache. Covers the Save-Changes auto-sync flow (where the
  // server-side handler refreshes entries and emits entry_updated
  // without an actorEmail), and any other shared-entry flow where
  // another tenant member updates.
  useEffect(() => {
    if (!tenantId || !selectedEntryId) return
    return onKeybookEvent(event => {
      if (event.tenantId !== tenantId) return
      if (event.type !== 'keybook:entry_updated') return
      if (event.entryId !== selectedEntryId) return
      setDecryptedData(null)
      decryptInFlight.current = null
      decryptGen.current++
      setDecryptEpoch(n => n + 1)
    })
  }, [tenantId, selectedEntryId, onKeybookEvent])

  // GitHub install completion — dialog opens the popup, this listener
  // owns the post-install side effect (refresh + toast) so it works
  // whether the dialog is still mounted or already closed.
  //
  // BroadcastChannel is the primary path. window.opener is unreliable
  // after a GitHub round-trip (COOP severs it), so postMessage stays
  // as a best-effort backup only.
  useEffect(() => {
    const seen = new Set<string>()
    const handle = (data: { entryId?: string; account?: string; existing?: boolean; accountType?: string }) => {
      // Dedup so the BC and postMessage paths don't both toast.
      const key = data.entryId || `__${Date.now()}`
      if (seen.has(key)) return
      seen.add(key)
      keybook.refreshEntries()
      // The OAuth callback re-encrypts the entry with fresh tokens
      // and refreshed repo metadata. The list update above pulls new
      // EntryMetadata, but the modal's decryptedData cache is
      // separate — invalidate so the auto-decrypt useEffect re-fires.
      setDecryptedData(null)
      decryptInFlight.current = null
      decryptGen.current++
      setDecryptEpoch(n => n + 1)
      toast.success(`Authorized: ${data.account ?? 'GitHub'}`)
      // A key made from this tab's New Entry dialog is shown like any other new key
      // (the dialog's onSave never runs for it — the popup created it). `existing`
      // is not "the key existed" (it says the App was already installed), so it
      // isn't consulted; a re-authorized key being focused is harmless.
      if (data.entryId && showCreateDialogRef.current) {
        setSearchQuery('')
        setCollectionFilter('all')
        setFocusEntryId(data.entryId)
      }
    }

    // Auto-refresh handler for "user just saved install changes on
    // github.com" events fired by the post-Save friendly page. If
    // the currently-selected entry matches the changed install,
    // call refresh-metadata so the modal reflects the new scope /
    // repo list without a manual click.
    const handleInstallChanged = async (installationId: string) => {
      if (!installationId) return
      if (selectedEntry?.credentialType !== 'github') return
      // Refresh writes the key: write or manage (the server refuses otherwise).
      if (selectedEntry.permission !== 'write' && selectedEntry.permission !== 'manage') return
      if (decryptedData?.fields?.installation_id !== installationId) return
      const reload = () => {
        setDecryptedData(null)
        decryptInFlight.current = null
        decryptGen.current++
        setDecryptEpoch(n => n + 1)
        keybook.refreshEntries()
      }
      try {
        const { keybookApi } = await import('../client/api.js')
        await keybookApi.refreshGithubMetadata(selectedEntry.id)
        reload()
        toast.success('Synced GitHub changes.')
      } catch (err: any) {
        // The key changed first — usually the server's own install auto-sync
        // (same GitHub callback). Not an error: show the current fields.
        if (err?.code === 'KEYBOOK_ENTRY_CHANGED') {
          reload()
          return
        }
        toast.error(err?.message || 'Failed to sync from GitHub')
      }
    }

    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel('papan-github-install')
      ch.onmessage = (ev) => {
        if (ev.data?.type === 'installed') handle(ev.data)
        else if (ev.data?.type === 'install-changed') handleInstallChanged(ev.data.installationId)
      }
    } catch { /* BroadcastChannel unsupported — fall back to postMessage */ }

    function onMessage(ev: MessageEvent) {
      if (ev.data?.type === 'keybook-github-installed') handle(ev.data)
      else if (ev.data?.type === 'keybook-github-install-changed') handleInstallChanged(ev.data.installationId)
    }
    window.addEventListener('message', onMessage)

    return () => {
      ch?.close()
      window.removeEventListener('message', onMessage)
    }
  }, [keybook, toast, selectedEntry, decryptedData])

  // The key just created: once its fob is rendered (the entries refreshed before createEntry returned, but a section,
  // a filter or the sort may still be settling), scroll it to the middle of the view and focus its name — the fob's
  // own button — then mark it for a moment so the eye finds it.
  useEffect(() => {
    if (!focusEntryId) return
    const fob = document.querySelector<HTMLElement>(`[data-testid="key-card"][data-entry-id="${CSS.escape(focusEntryId)}"]`)
    if (!fob) return
    fob.scrollIntoView({ block: 'center', behavior: 'smooth' })
    fob.querySelector<HTMLElement>('[data-testid="key-card-name"]')?.focus({ preventScroll: true })
    setFocusEntryId(null)
    setJustCreatedId(focusEntryId)
  })
  useEffect(() => {
    if (!justCreatedId) return
    const clear = setTimeout(() => setJustCreatedId(null), 2500)
    return () => clearTimeout(clear)
  }, [justCreatedId])

  /** The currently-open collection folder (null = top-level gallery). */
  const openCollection = collectionFilter !== 'all'
    ? collectionOptions.find(c => c.id === collectionFilter) ?? null
    : null

  const isMineEntry = useCallback((e: EntryMetadata) =>
    e.ownerType === 'email' && !!callerEmailLower && e.ownerId === callerEmailLower,
  [callerEmailLower])

  /** What the gallery shows: everything readable, minus the keys read only
   *  through the admin role while "Show as admin" is off. The filter applies
   *  only where the switch is offered (`adminView`) — a personal drive (or a
   *  non-admin) has no switch to turn them back on, so it shows everything
   *  readable even if the server flags some keys. */
  const showAllKeys = !adminView || showAsAdmin
  const shownEntries = showAllKeys ? keybook.entries : keybook.entries.filter(e => !e.hiddenWithoutAdminView)

  // Inside an OPEN collection every key you can read is listed — opening it is
  // explicit, and its header counts all of them (a body saying "no keys yet"
  // under a header that counts 3 contradicted itself).
  const filteredEntries = sortKeys((openCollection ? keybook.entries : shownEntries).filter(e => {
    if (openCollection && e.collectionId !== openCollection.id) return false
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    // The shared name too — a nickname replaces `label`, but teammates (and
    // the card's engraving) know the key by its shared name.
    return e.label.toLowerCase().includes(q)
      || (e.sourceLabel ?? '').toLowerCase().includes(q)
      || (e.usernameHint || '').toLowerCase().includes(q)
  }), keySort)

  /** Keys per collection — the case tiles preview the actual keys inside
   *  (colored dots in each key's hue) instead of a bare count. Built from
   *  EVERY readable key, not the switch-filtered gallery: the viewer can read
   *  them all, and a count of "empty" beside Delete would hide what the
   *  delete ungroups. */
  const collectionEntries = groupByCollection(keybook.entries)
  /** The same, over what the gallery shows — decides only whether a tile read
   *  through the admin role still appears with "Show as admin" off. */
  const shownCollectionEntries = groupByCollection(shownEntries)

  /** Gallery sections — grouped by OWNERSHIP so "genuinely shared with
   *  me" never blurs into "visible because I'm an admin": your own keys,
   *  the keys shared with you, and — only under "Show as admin" — the rest
   *  of the org (read only through the admin role). Inside an open folder
   *  (or on a personal tenant) the split adds nothing — one flat section. */
  /** Collection tiles: one read only through the admin role shows under "Show
   *  as admin" — or when a key you see without it is filed there. */
  const shownCollections = collectionOptions.filter(c =>
    showAllKeys || !c.hiddenWithoutAdminView || shownCollectionEntries.has(c.id))

  const sections: Array<{ id: string; name: string | null; Icon: typeof User | null; entries: EntryMetadata[] }> = (() => {
    if (openCollection || !isOrg) {
      return filteredEntries.length ? [{ id: 'flat', name: null, Icon: null, entries: filteredEntries }] : []
    }
    const mine = filteredEntries.filter(isMineEntry)
    const shared = filteredEntries.filter(e => !isMineEntry(e) && !e.hiddenWithoutAdminView)
    // Present only while "Show as admin" is on (`shownEntries`).
    const orgRest = filteredEntries.filter(e => !isMineEntry(e) && e.hiddenWithoutAdminView)
    const out: Array<{ id: string; name: string | null; Icon: typeof User | null; entries: EntryMetadata[] }> = []
    if (mine.length) out.push({ id: 'yours', name: t('sections.yourKeys'), Icon: User, entries: mine })
    if (shared.length) out.push({ id: 'shared', name: t('sections.sharedWithYou'), Icon: Users, entries: shared })
    if (orgRest.length) out.push({ id: 'org', name: t('sections.orgRest'), Icon: Building2, entries: orgRest })
    return out
  })()

  /** One key on the rack — a vertical hanging fob. Identity reads top
   *  to bottom the way you'd read a physical key: connector (CSS) →
   *  what it connects to (icon) → its name → its kind → who owns it. */
  const renderKeyFob = (entry: EntryMetadata) => {
    const led = cardLedState(entry)
    const isOrphan = entry.ownerType !== 'role' && !entry.ownerActive
    const canShare = isOrg && entry.permission === 'manage'
    const ConnIcon = connectorIcon(entry)
    const caseName = !openCollection && entry.collectionId
      ? collectionOptions.find(c => c.id === entry.collectionId)?.name
      : undefined
    // A container, not a button (so the ⋯ can be its own keyboard-reachable button): the mouse opens the key from anywhere on the
    // fob; the NAME slot is the fob's real button (keyboard, accessible name); the
    // share control is its own sibling button — never one nested in another.
    return (
      <div
        key={entry.id}
        onClick={() => setSelectedEntryId(entry.id)}
        className={`kbg-fob group${entry.id === justCreatedId ? ' ring-2 ring-[var(--kb-gold,#d4a843)] ring-offset-2 ring-offset-transparent' : ''}`}
        style={{ '--pc-hue': fobHue(entry) } as CSSProperties}
        data-testid="key-card"
        data-entry-id={entry.id}
        data-just-created={entry.id === justCreatedId ? 'true' : undefined}
      >
        <span className="kbg-pinhead" aria-hidden />
        <span className="kbg-fob-cap">
        <span aria-hidden className="ws-project-glyph kbg-glyph shrink-0">
          <ConnIcon size={16} />
        </span>
        <span className="w-full text-[9px] uppercase tracking-widest themed-text-muted truncate">
          {credentialTypeLabel(entry.credentialType)}
          {entry.usernameHint ? ` · ${entry.usernameHint}` : ''}
        </span>
        {/* Carved name slot — the shared name engraved in it; a personal
            nickname (if set) is a paper insert that slides out of the
            slot's bottom while the LABEL is hovered. */}
        <button type="button" className="kbg-name px-1 w-full bg-transparent border-0 cursor-pointer" data-testid="key-card-name">
          <span className="text-[13px] font-medium themed-text leading-snug line-clamp-2 break-words">
            {entry.sourceLabel ?? entry.label}
            {isOrphan && (
              <span
                data-testid="entry-orphan-badge"
                title="Owner email no longer maps to an active member"
                className="inline-flex align-middle ml-1 text-amber-400"
              >
                <AlertCircle className="w-3 h-3" />
              </span>
            )}
          </span>
          {entry.customLabel && (
            <span className="kbg-name-nick" title={t('inspector.nicknameSet', { name: entry.customLabel })}>
              <span className="truncate">{entry.customLabel}</span>
            </span>
          )}
        </button>
        {/* Share sits right under the label — revealed on hover or focus, its
            slot always reserved so nothing jumps. A real button, a sibling of
            the name's, in the tab order. */}
        {canShare ? (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              setSharingEntry({ id: entry.id, label: entry.sourceLabel ?? entry.label, credentialType: entry.credentialType })
            }}
            className="h-6 px-2 rounded-md inline-flex items-center gap-1 text-[10px] themed-text-muted kbg-reveal hover:bg-black/25 hover:themed-icon-active transition-opacity cursor-pointer"
            title={t('access.manageTitle')}
            // Access is about sharing with others, who know the key by its SHARED
            // name — never the caller's private nickname.
            aria-label={t('access.manageAria', { name: entry.sourceLabel ?? entry.label })}
          >
            <Share2 size={12} />
            {t('share')}
          </button>
        ) : (
          <span className="h-6" aria-hidden />
        )}
        <span className="mt-auto w-full flex flex-col items-center gap-1.5 min-w-0">
          {/* Owner avatar — bottom cluster, riding slightly higher than
              the old row; the status LED (lit states only) docks on its
              corner like a presence dot. */}
          <span className="relative mb-1.5" title={ownerText(entry.ownerId)}>
            <span className={`kb-key-avatar ${avatarBg(entry.ownerId)}`} aria-hidden>
              {getInitials(erasedOwnerEmail(entry.ownerId) ?? entry.ownerId)}
            </span>
            {led !== 'off' && (
              <span
                className="kbg-led absolute -bottom-0.5 -right-0.5"
                data-state={led}
                title={led === 'warn' ? t('rack.ledWarn') : t('rack.ledInUse')}
                aria-hidden
              />
            )}
          </span>
          {/* Owner identity — only under "Show as admin"; otherwise
              everything shown is implicitly mine-or-shared.
              Plain lowercase text (the e2e asserts the raw email). An
              email that never fits on one 156px line wraps onto a second
              (break-all: the distinguishing part is the local part's tail)
              and the full address rides on the title — an ellipsis after
              nine characters told nobody anything. */}
          {adminView && showAsAdmin && (
            <span
              data-testid="entry-owner-badge"
              className="max-w-full inline-flex items-start gap-1 text-[10px] themed-text-muted min-w-0 leading-tight"
              title={entry.ownerType === 'team' ? (teamNames[entry.ownerId] ?? entry.ownerId)
                : entry.ownerType === 'role' ? `Role: ${entry.ownerId}`
                : ownerText(entry.ownerId)}
            >
              {entry.ownerType === 'team' ? <Users size={9} className="shrink-0 mt-px" />
                : entry.ownerType === 'role' ? <Tag size={9} className="shrink-0 mt-px" />
                : <AtSign size={9} className="shrink-0 mt-px" />}
              <span className="line-clamp-2 break-all text-left">
                {entry.ownerType === 'team' ? (teamNames[entry.ownerId] ?? entry.ownerId)
                  : entry.ownerType === 'role' ? `Role: ${entry.ownerId}`
                  : ownerText(entry.ownerId)}
              </span>
            </span>
          )}
          {/* The very bottom of the cap: the collection name (plain text)
              pinched between two circuit traces — or, when unfiled, the
              traces alone as a circuit divider. Same anatomy either way. */}
          <span className="kbg-case-row">
            <span className="kbg-case-line" data-side="l" aria-hidden />
            {caseName && (
              <span className="text-[9px] uppercase tracking-wider themed-text-muted truncate max-w-[80%]" title={caseName}>
                {caseName}
              </span>
            )}
            <span className="kbg-case-line" data-side="r" aria-hidden />
          </span>
        </span>
        </span>
      </div>
    )
  }

  /** The dashed "cut a new key" slot hanging at the end of the rack. */
  const renderGhostFob = () => (
    <button
      key="__ghost"
      onClick={() => setShowCreateDialog(true)}
      className="kbg-fob kbg-fob-new group"
      aria-label={t('rack.cutNewKey')}
      title={t('newEntry')}
    >
      <span className="kbg-pinhead" aria-hidden />
      <span className="kbg-fob-cap justify-center">
        <span aria-hidden className="ws-project-glyph kbg-glyph opacity-60 group-hover:opacity-100 transition-opacity">
          <Plus size={16} />
        </span>
        <span className="text-xs themed-text-muted">{t('rack.cutNewKey')}</span>
      </span>
    </button>
  )

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* ── Floating header on the amber hero — spacing separates, no rules.
            Two rows, as the drive's Projects page: identity + the one
            primary action, then the controls. ── */}
      <header className="px-6 pt-5 pb-1">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <span aria-hidden className="ws-project-glyph kbg-glyph">
              <BookKey size={17} />
            </span>
            <h1 className="text-xl font-semibold themed-text leading-tight truncate">{t('title')}</h1>
            {/* The gallery's count: the keys LISTED under it (with "Show as
                admin" off, keys read only through that role are not listed).
                Collection counts are different on purpose — a collection's
                keys are all its readable keys. */}
            {hasLoaded && !keybook.loadFailed && (
              <span className="text-xs themed-text-muted mt-0.5" data-testid="keybook-count">{shownEntries.length}</span>
            )}
            {/* Keybook settings (keybook-wide access today) — for whoever
                manages the keybook, per the server's answer. */}
            {managesRoot && isOrg && (
              <Tooltip content={t('settings.title')}>
                <button
                  type="button"
                  onClick={() => setShowRootAccess(true)}
                  aria-label={t('settings.title')}
                  data-testid="open-keybook-settings"
                  className="kbg-btn w-7 h-7 rounded-full shrink-0"
                >
                  <Settings size={14} />
                </button>
              </Tooltip>
            )}
          </div>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="themed-btn-primary h-8 px-3.5 rounded-full text-sm flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
          >
            <Plus size={15} />
            {t('newEntry')}
          </button>
        </div>

        {/* The intro, between the title and the controls: one line always,
            the four points collapsible (collapsed until opened, then as the
            user left it). Plain words: encrypted before it's saved, the
            encryption keys protected by AWS KMS (production always runs
            KMS; KMS unwraps the master key at boot and the server keeps it
            in memory — so never "held in KMS" or "never on disk"), opened only by
            people given access, used by agents and apps without pasting,
            every change logged. Nothing about what the server can't do —
            it decrypts to serve. */}
        <section
          className="kbg-well rounded-2xl px-4 py-3 mt-3"
          aria-label={t('intro.protectButton')}
          data-testid="keybook-intro"
        >
          <div className="flex items-center gap-3 min-w-0">
            <ShieldCheck size={16} className="shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} aria-hidden />
            <p className="flex-1 min-w-0 text-sm themed-text leading-snug">
              {t(isOrg ? 'intro.leadOrg' : 'intro.leadPersonal')}
            </p>
            <button
              type="button"
              onClick={toggleIntro}
              aria-expanded={introOpen}
              data-testid="keybook-protection"
              className="shrink-0 inline-flex items-center gap-1 text-xs themed-text-muted hover:themed-text transition-colors cursor-pointer"
            >
              {t('intro.protectButton')}
              <ChevronDown size={14} className={`transition-transform ${introOpen ? 'rotate-180' : ''}`} aria-hidden />
            </button>
          </div>
          {introOpen && (
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2.5 mt-3 pl-7" data-testid="keybook-intro-points">
              {([
                { Icon: Lock, title: 'intro.encryptedTitle', body: 'intro.encryptedBody' },
                { Icon: Users, title: isOrg ? 'intro.accessTitleOrg' : 'intro.accessTitle', body: isOrg ? 'intro.accessBodyOrg' : 'intro.accessBodyPersonal' },
                { Icon: Bot, title: 'intro.useTitle', body: 'intro.useBody' },
                { Icon: History, title: 'intro.logTitle', body: 'intro.logBody' },
              ] as const).map(({ Icon, title, body }) => (
                <li key={title} className="flex items-start gap-2 min-w-0">
                  <Icon size={13} className="mt-0.5 shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} aria-hidden />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium themed-text">{t(title)}</span>
                    <span className="block text-xs themed-text-muted leading-snug">{t(body)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Controls: search · refresh — and "Show as admin" pinned
            right, where the Projects / PDApps pages keep theirs. */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" aria-hidden />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full themed-input ws-drive-pill rounded-full pl-9 pr-3.5 py-1.5 text-sm"
            />
          </div>
          <KeySortMenu sort={keySort} onChange={changeKeySort} />
          <button
            onClick={() => keybook.refreshEntries()}
            className="kbg-btn w-8 h-8 rounded-full"
            title={t('rack.refresh')}
            aria-label="Refresh"
          >
            <RefreshCw size={14} className={keybook.isLoading ? 'animate-spin' : ''} />
          </button>
          {/* "Show as admin" — the Projects / PDApps pages' switch: off hides
              the keys this admin (or the owner) reads only through that role. */}
          {adminView && (
            <button
              onClick={() => { setShowAsAdmin(v => !v); setSelectedEntryId(null) }}
              role="switch"
              aria-checked={showAsAdmin}
              data-testid="keybook-show-admin"
              className="flex items-center gap-2 shrink-0 themed-text-muted ml-auto"
            >
              <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)] ${showAsAdmin ? 'bg-violet-500' : 'themed-bg-primary'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.35)] transition-all ${showAsAdmin ? 'left-[18px]' : 'left-0.5'}`} />
              </span>
              <span className="text-xs">{t('showAsAdmin')}</span>
            </button>
          )}
        </div>
      </header>

      {/* ── The rack — collection cases, then keys hanging by ownership ── */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-4"
        data-testid="keybook-list"
        // Busy until the held list is this view's — not just while a fetch runs
        // (before the first fetch starts isLoading is still false).
        aria-busy={keybook.isLoading || !keybook.hasLoaded}
      >
        {/* Placeholder on the FIRST load only — a refresh (a nickname, a
            bind, another member's change) keeps the rack mounted, so its
            scroll position and the detail's focus return survive. */}
        {!hasLoaded ? (
          <div className="h-full flex items-center justify-center themed-text-muted text-sm">
            Loading...
          </div>
        ) : (
          <>

            {/* Folder strip (top level) / folder header (inside one). Folders
                are entry points, not containers — keys stay visible in the
                ownership sections below so nothing hides behind a click. */}
            {openCollection ? (
              <div className="flex items-center gap-2.5 mb-1">
                <button
                  onClick={() => setCollectionFilter('all')}
                  className="kbg-btn w-8 h-8 rounded-full shrink-0"
                  title={t('rack.allKeys')}
                  aria-label={t('rack.allKeys')}
                >
                  <ArrowLeft size={15} />
                </button>
                <Folder size={18} className="shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} aria-hidden />
                <div className="min-w-0 mr-auto">
                  {renamingCollection?.id === openCollection.id ? (
                    renderCollectionRenameInput('w-64 max-w-full px-2 py-1 font-semibold')
                  ) : (
                    <div className="text-sm font-semibold themed-text truncate">{openCollection.name}</div>
                  )}
                  <div className="flex items-center gap-1 text-[11px] themed-text-muted">
                    <Key size={9} aria-hidden />
                    {(collectionEntries.get(openCollection.id) ?? []).length}
                    <span aria-hidden>·</span>
                    {t(`collections.level.${openCollection.permission}`, { defaultValue: openCollection.permission })}
                  </div>
                </div>
                {/* The same three actions as the tile's menu — a manager
                    inside the folder isn't sent back to the strip for them. */}
                {openCollection.permission === 'manage' && (
                  <>
                    {isOrg && <button
                      onClick={() => setSharingCollection({ id: openCollection.id, name: openCollection.name })}
                      className="kbg-btn h-8 px-3 text-xs shrink-0"
                      data-testid="collection-folder-share"
                    >
                      <Users size={13} />
                      {t('access.manageTitle')}
                    </button>}
                    <button
                      onClick={() => setRenamingCollection({ id: openCollection.id, value: openCollection.name })}
                      className="kbg-btn h-8 px-3 text-xs shrink-0"
                      data-testid="collection-folder-rename"
                    >
                      <Pencil size={13} />
                      {t('collections.rename')}
                    </button>
                    <button
                      onClick={() => void deleteCollection({ id: openCollection.id, name: openCollection.name })}
                      className="kbg-btn h-8 px-3 text-xs shrink-0 hover:text-red-400"
                      data-testid="collection-folder-delete"
                    >
                      <Trash2 size={13} />
                      {t('delete')}
                    </button>
                  </>
                )}
              </div>
            ) : (shownCollections.length > 0 || managesRoot) && (
              /* The strip renders for anyone with a collection to show, and
                 for a root manager even with none — the Add tile IS the
                 empty state. For everyone else an empty rail they can't act
                 on is noise, so there is none. */
              <div className="mb-6">
                <Rail className="mb-3">
                  <Library size={10} aria-hidden />
                  {t('sections.collections')}
                </Rail>
                {/* Centered like the key racks below — one alignment system
                    for the whole view. */}
                <div className="grid grid-cols-[repeat(auto-fit,220px)] justify-center gap-2.5">
                  {shownCollections.map(c => {
                    const inside = collectionEntries.get(c.id) ?? []
                    const dots = (
                      /* The keys inside, peeking out of the case — one dot
                         per key, in that key's identity hue — and the
                         viewer's own level on the case, as a muted suffix
                         (never baked into the name). */
                      <span className="mt-1 flex items-center gap-[3px] min-w-0">
                        {inside.slice(0, 8).map(e => (
                          <span
                            key={e.id}
                            className="kbg-case-dot"
                            style={{ '--pc-hue': fobHue(e) } as CSSProperties}
                            aria-hidden
                          />
                        ))}
                        <span className="text-[10px] themed-text-muted ml-0.5 truncate">
                          {inside.length === 0 ? t('rack.caseEmpty') : inside.length > 8 ? `+${inside.length - 8}` : inside.length}
                          {' · '}
                          {t(`collections.level.${c.permission}`, { defaultValue: c.permission })}
                        </span>
                      </span>
                    )
                    if (renamingCollection?.id === c.id) {
                      return (
                        <div key={c.id} className="ws-home-tile" data-testid="collection-folder" data-renaming="">
                          <Folder size={17} className="shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} aria-hidden />
                          <span className="flex-1 min-w-0">
                            {renderCollectionRenameInput('w-full px-2 py-0.5')}
                            {dots}
                          </span>
                        </div>
                      )
                    }
                    // The ⋯ (and right-click) for whoever may do something with it.
                    const menuable = c.permission === 'manage' || canSeeKeybookAccess(c.permission as 'read' | 'write' | 'manage', collectionsAccessCapability)
                    // A container holding TWO sibling buttons (both keyboard-reachable): the tile's own
                    // action (open the collection) and the ⋯ — a real, focusable
                    // button, never nested inside another.
                    return (
                      <div
                        key={c.id}
                        className="ws-home-tile group"
                        data-testid="collection-folder"
                        onClick={e => { if (e.target === e.currentTarget) setCollectionFilter(c.id) }}
                        onContextMenu={menuable ? (e => {
                          e.preventDefault()
                          setCollectionMenu({ id: c.id, name: c.name, permission: c.permission, x: e.clientX, y: e.clientY })
                        }) : undefined}
                      >
                        <button
                          type="button"
                          onClick={() => setCollectionFilter(c.id)}
                          className="flex-1 min-w-0 flex items-center gap-2.5 text-left cursor-pointer"
                          title={t('rack.filterByCollection', { name: c.name })}
                          data-testid="collection-open"
                        >
                          <Folder size={17} className="shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} aria-hidden />
                          <span className="flex-1 min-w-0">
                            {/* Two lines before an ellipsis — "Platform (mana…"
                                told nobody which collection this was. */}
                            <span className="block text-sm themed-text line-clamp-2 break-words leading-snug">{c.name}</span>
                            {dots}
                          </span>
                        </button>
                        {/* ⋯ — the tile's glass menu, revealed on hover / focus;
                            right-click opens the same menu at the cursor. */}
                        {menuable && (
                          <Tooltip content={t('collections.menuAria', { name: c.name })}>
                            <button
                              type="button"
                              aria-haspopup="menu"
                              aria-expanded={collectionMenu?.id === c.id}
                              onClick={e => {
                                const el = e.currentTarget as HTMLElement
                                const r = el.getBoundingClientRect()
                                collectionMenuTrigger.current = el
                                setCollectionMenu(m => m?.id === c.id ? null : { id: c.id, name: c.name, permission: c.permission, x: r.left, y: r.bottom + 4 })
                              }}
                              className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 themed-text-muted kbg-reveal hover:themed-bg-tertiary hover:themed-icon-active transition-opacity cursor-pointer"
                              aria-label={t('collections.menuAria', { name: c.name })}
                              data-testid="collection-menu"
                            >
                              <MoreHorizontal size={14} />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    )
                  })}
                  {/* The dashed ghost tile — the strip's "cut a new key":
                      an inline name field, Enter creates, Esc lets go. */}
                  {managesRoot && (
                    addingCollection ? (
                      <div className="ws-home-tile" data-testid="collection-add-tile">
                        <Folder size={17} className="shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} aria-hidden />
                        <input
                          autoFocus
                          value={newCollectionName}
                          onChange={e => setNewCollectionName(e.target.value)}
                          onKeyDown={e => {
                            e.stopPropagation()
                            if (e.key === 'Enter') void createCollection()
                            else if (e.key === 'Escape') { setAddingCollection(false); setNewCollectionName('') }
                          }}
                          // A click elsewhere with nothing typed lets go; typed
                          // text stays (the ✓ is right there).
                          onBlur={() => { if (!newCollectionName.trim()) setAddingCollection(false) }}
                          placeholder={t('collections.namePlaceholder')}
                          disabled={collectionBusy}
                          aria-label={t('collections.add')}
                          data-testid="collection-add-input"
                          className="themed-input flex-1 min-w-0 px-2 py-1 rounded-md text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => void createCollection()}
                          disabled={!newCollectionName.trim() || collectionBusy}
                          className="kbg-btn w-7 h-7 rounded-md shrink-0"
                          aria-label={t('collections.add')}
                          data-testid="collection-add-confirm"
                        >
                          {collectionBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingCollection(true)}
                        className="ws-home-tile group"
                        // The ghost recipe (as the ghost fob): no fill, no lift,
                        // the hairline dashed. Inline — `.ws-home-tile` is
                        // unlayered CSS, which a layered utility can't override.
                        style={{ background: 'transparent', boxShadow: 'none', borderStyle: 'dashed' }}
                        data-testid="collection-add"
                      >
                        <span aria-hidden className="ws-project-glyph kbg-glyph shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" style={{ width: 28, height: 28, borderRadius: 8 }}>
                          <Plus size={14} />
                        </span>
                        <span className="text-sm themed-text-muted group-hover:themed-text transition-colors">{t('collections.add')}</span>
                      </button>
                    )
                  )}
                </div>
              </div>
            )}

            {sections.length === 0 ? (
              searchQuery ? (
                <div className="flex flex-col items-center justify-center gap-3 themed-text-muted py-16">
                  <span aria-hidden className="kbg-well w-16 h-16 rounded-2xl flex items-center justify-center">
                    <BookKey size={28} style={{ color: 'var(--kb-gold, #d4a843)' }} className="opacity-80" />
                  </span>
                  <p className="text-sm">No matching credentials</p>
                </div>
              ) : (
                /* Empty rack — one bare rail with the ghost key hanging on
                   it. The rack itself is the empty state. */
                <div>
                  <Rail>
                    <Key size={10} aria-hidden />
                    0
                  </Rail>
                  {/* Inside a folder a new key would land unfiled — no ghost
                      slot there, and the text says the FOLDER is empty. */}
                  {!openCollection && (
                    <div className="grid grid-cols-[repeat(auto-fit,156px)] justify-center gap-x-4 gap-y-4 pt-2">
                      {renderGhostFob()}
                    </div>
                  )}
                  <p className="text-sm themed-text-muted mt-6">
                    {openCollection && !keybook.loadFailed ? t('rack.collectionEmpty') : keybook.loadFailed ? (
                      <>
                        {t('loadFailed')}{' '}
                        <button type="button" onClick={() => { keybook.refreshEntries().catch(() => {}) }} className="underline underline-offset-2 hover:themed-text cursor-pointer">
                          {t('retry')}
                        </button>
                      </>
                    ) : t('noEntries')}
                  </p>
                </div>
              )
            ) : (
              sections.map(section => (
                <div key={section.id} className="mt-7 first:mt-0">
                  <Rail>
                    {section.Icon ? <section.Icon size={10} aria-hidden /> : <Key size={10} aria-hidden />}
                    {section.name ?? section.entries.length}
                    {section.name ? ` · ${section.entries.length}` : ''}
                  </Rail>
                  {/* No ghost slot on a populated rail: with keys hanging it
                      was a fifth column of nothing beside New Entry. The
                      empty rack (above) still hangs one. */}
                  <div className="grid grid-cols-[repeat(auto-fit,156px)] justify-center gap-x-4 gap-y-4 pt-2">
                    {section.entries.map(renderKeyFob)}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* ── The detail — the selected key on a centered glass modal ── */}
      {selectedEntry && createPortal(
        // The app's modal band (ConfirmModal's note: share/access dialogs at
        // z-[100]/z-[110]) — above the mobile header (z-[60]); its child
        // dialogs sit at z-[110], popovers z-[320], the confirm z-[400].
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedEntryId(null)} />
          <div
            ref={detailRef}
            tabIndex={-1}
            className={`${themeClass} ws-glass-dialog relative z-10 w-full max-w-3xl max-h-[88vh] flex flex-col outline-none`}
            role="dialog"
            aria-modal="true"
            aria-label={selectedEntry.sourceLabel ?? selectedEntry.label}
            data-testid="key-detail"
          >
            {/* Modal header — title/rename, chips, meta, then the key
                itself: a hero fob with the serial engraved on its body. */}
            <header className="flex items-start gap-3 px-6 pt-5 pb-3">
              <div className="flex-1 min-w-0">
                {renamingTitle !== null ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <input
                      autoFocus
                      type="text"
                      value={renamingTitle}
                      onChange={(e) => setRenamingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        // Handled here — it must not also reach the modal's
                        // window listener (Escape would close the whole detail).
                        if (e.key === 'Enter') { e.stopPropagation(); handleSaveRename(selectedEntry.id, selectedEntry.sourceLabel ?? selectedEntry.label) }
                        else if (e.key === 'Escape') { e.stopPropagation(); setRenamingTitle(null) }
                      }}
                      className="themed-input flex-1 min-w-0 px-2 py-1 rounded text-base font-semibold"
                    />
                    <button
                      type="button"
                      onClick={() => handleSaveRename(selectedEntry.id, selectedEntry.sourceLabel ?? selectedEntry.label)}
                      className="kbg-btn w-7 h-7 rounded-full flex-shrink-0"
                      aria-label="Save rename"
                      title="Save"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenamingTitle(null)}
                      className="kbg-btn w-7 h-7 rounded-full flex-shrink-0"
                      aria-label="Cancel rename"
                      title="Cancel (Esc)"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 group min-w-0">
                    {/* role+aria-level preserves heading semantics
                        while letting Tooltip's span wrapper handle
                        the overflow detection. */}
                    <Tooltip
                      content={selectedEntry.sourceLabel ?? selectedEntry.label}
                      className="text-base font-semibold themed-text truncate min-w-0 block"
                    >
                      <span role="heading" aria-level={2}>
                        {selectedEntry.sourceLabel ?? selectedEntry.label}
                      </span>
                    </Tooltip>
                    {canManageSelected && (
                    <button
                      type="button"
                      onClick={() => setRenamingTitle(selectedEntry.sourceLabel ?? selectedEntry.label)}
                      className="p-1 themed-text-muted hover:themed-text rounded transition-colors flex-shrink-0 cursor-pointer kbg-reveal"
                      aria-label="Rename entry"
                      title="Rename"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    )}
                  </div>
                )}
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="kbg-pill px-2 py-0.5 text-[10px] uppercase tracking-wide whitespace-nowrap flex-shrink-0">
                    {credentialTypeLabel(selectedEntry.credentialType)}
                  </span>
                  {selectedEntry.provider && selectedEntry.credentialType !== 'github' && (
                    <span className="kbg-pill px-2 py-0.5 text-[10px] uppercase tracking-wide whitespace-nowrap flex-shrink-0">
                      {selectedEntry.provider}
                    </span>
                  )}
                  {/* Personal nickname — a per-member alias, editable
                      inline. The gallery shows it in place of the shared
                      label for this member only. */}
                  {editingNickname !== null ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        autoFocus
                        type="text"
                        value={editingNickname}
                        placeholder={t('inspector.nicknamePlaceholder')}
                        onChange={e => setEditingNickname(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.stopPropagation(); handleSaveNickname(selectedEntry.id) }
                          else if (e.key === 'Escape') { e.stopPropagation(); setEditingNickname(null) }
                        }}
                        className="themed-input px-2 py-0.5 rounded text-[11px] w-36"
                      />
                      <button
                        type="button"
                        onClick={() => handleSaveNickname(selectedEntry.id)}
                        className="p-0.5 themed-icon hover:themed-icon-active cursor-pointer"
                        aria-label={t('inspector.saveNickname')}
                      >
                        <Check className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingNickname(null)}
                        className="p-0.5 themed-text-muted hover:themed-text cursor-pointer"
                        aria-label="Cancel"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingNickname(selectedEntry.customLabel ?? '')}
                      className="kbg-pill px-2 py-0.5 text-[10px]"
                      title={t('inspector.nicknameHint')}
                      data-testid="entry-nickname"
                    >
                      {selectedEntry.customLabel
                        ? t('inspector.nicknameSet', { name: selectedEntry.customLabel })
                        : t('inspector.setNickname')}
                    </button>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs themed-text-muted min-w-0">
                  <span className="inline-flex items-center gap-1 min-w-0">
                    {selectedEntry.ownerType === 'team' ? <Users size={11} /> : selectedEntry.ownerType === 'role' ? <Tag size={11} /> : <AtSign size={11} />}
                    <span className="truncate">{selectedEntry.ownerType === 'team' ? (teamNames[selectedEntry.ownerId] ?? 'Team') : selectedEntry.ownerType === 'role' ? `Role: ${selectedEntry.ownerId}` : ownerText(selectedEntry.ownerId)}</span>
                    {selectedEntry.ownerType === 'email'
                      && !!callerEmailLower
                      && selectedEntry.ownerId === callerEmailLower && (
                      <span
                        data-testid="entry-detail-mine-badge"
                        title="You own this credential"
                        className="ws-chip-mine ml-1 flex-shrink-0"
                      >
                        you
                      </span>
                    )}
                  </span>
                  <span aria-hidden>·</span>
                  <span>created {new Date(selectedEntry.createdAt).toLocaleDateString()}</span>
                  {selectedEntry.lastUsedAt && <>
                    <span aria-hidden>·</span>
                    <span>last used {new Date(selectedEntry.lastUsedAt).toLocaleDateString()}</span>
                  </>}
                  <span aria-hidden>·</span>
                  {/* Key ID — deliberately small: one click copies it.
                      PDApps / scripts bind by this handle. */}
                  <button
                    type="button"
                    data-testid="entry-key-id"
                    onClick={() => handleCopyKeyId(selectedEntry.id)}
                    title={`${t('inspector.keyIdHint')} ${t('inspector.clickToCopy')}`}
                    className="kbg-id inline-flex items-center gap-1 min-w-0 max-w-[190px] text-[10px] themed-text-muted hover:themed-text cursor-pointer"
                  >
                    {copiedKeyId
                      ? <><Check className="w-3 h-3 text-green-400 shrink-0" />{t('inspector.keyIdCopied')}</>
                      : <span className="truncate">{selectedEntry.id}</span>}
                  </button>
                </div>
              </div>
              {/* NOT aria-label "Close" — that exact name belongs to the
                  access/collections dialogs (pinned by e2e). */}
              <button
                type="button"
                onClick={() => setSelectedEntryId(null)}
                className="kbg-btn w-7 h-7 rounded-full shrink-0"
                aria-label="Close details"
                title="Close (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
              {/* Two halves, on purpose: the CREDENTIAL itself on the
                  left, everything about managing it on the right. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div className="space-y-4 min-w-0">
              {/* ── Secrets ── */}
              <section className="space-y-2">
                <div className="text-[10px] themed-text-muted uppercase tracking-wider">
                  {t('inspector.secretsTitle')}
                </div>
                {(() => {
                  const fields = getDisplayFieldsForType(selectedEntry.credentialType, decryptedData)
                  if (decryptError) {
                    return <div className="text-xs text-red-400 p-3 rounded-[10px] bg-red-500/10">{decryptError}</div>
                  }
                  // For non-static types, fields are derived from the
                  // decrypted blob — show a loading hint until that
                  // arrives, then either the discovered fields or a
                  // friendly empty-state.
                  if (fields.length === 0
                      && !STATIC_SCHEMA_TYPES.has(selectedEntry.credentialType)
                      && !decryptedData) {
                    return <p className="text-xs themed-text-muted">Loading fields…</p>
                  }
                  if (fields.length === 0) {
                    if (selectedEntry.credentialType === 'github') {
                      return (
                        <GithubInstallationSummary fields={decryptedData?.fields ?? null} />
                      )
                    }
                    return <p className="text-xs themed-text-muted">No fields stored on this credential.</p>
                  }
                  return (
                    <div className="space-y-2">
                      {fields.map(({ name: fieldName }) => {
                        const value = decryptedData?.fields?.[fieldName]
                        const showValue = revealAll && value !== undefined
                        const isMultiline = (value?.includes('\n') || (value?.length ?? 0) > 80) ?? false
                        return (
                          <div key={fieldName} className="space-y-1">
                            <label className="block text-[10px] themed-text-muted uppercase tracking-wider">
                              {fieldName.replace(/_/g, ' ')}
                            </label>
                            {isMultiline && showValue ? (
                              <div className="relative">
                                <pre className="kbg-well text-xs themed-text font-mono break-all whitespace-pre-wrap px-2.5 py-1.5 pr-9 max-h-32 overflow-y-auto">
                                  {value}
                                </pre>
                                <button
                                  type="button"
                                  onClick={() => handleCopyField(fieldName)}
                                  className="absolute top-1.5 right-1.5 p-1 themed-text-muted hover:themed-text rounded transition-colors cursor-pointer"
                                  title="Copy"
                                  aria-label="Copy value"
                                >
                                  {copiedField === fieldName
                                    ? <Check className="w-3.5 h-3.5 text-green-400" />
                                    : <Copy className="w-3.5 h-3.5" />
                                  }
                                </button>
                              </div>
                            ) : (
                              <div className="kbg-well flex items-center gap-1.5 px-2.5 py-1.5">
                                <span className={`flex-1 min-w-0 text-sm font-mono break-all ${showValue ? 'themed-text' : 'themed-text-muted select-none'}`}>
                                  {showValue
                                    ? (value || <span className="themed-text-muted">(empty)</span>)
                                    : '••••••••••••'}
                                </span>
                                {showValue && (
                                  <button
                                    type="button"
                                    onClick={() => handleCopyField(fieldName)}
                                    className="p-0.5 themed-text-muted hover:themed-text rounded transition-colors flex-shrink-0 cursor-pointer"
                                    title="Copy"
                                    aria-label="Copy value"
                                  >
                                    {copiedField === fieldName
                                      ? <Check className="w-3.5 h-3.5 text-green-400" />
                                      : <Copy className="w-3.5 h-3.5" />
                                    }
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}

                {/* The key's note — part of the encrypted value, so it shows with the
                    values, after Reveal. A GitHub key has no Reveal (its summary isn't
                    secret), so its note row carries its own Show / Hide. */}
                {(() => {
                  const note = decryptedData?.note
                  if (!note?.trim()) return null
                  const show = revealAll
                  return (
                    <div className="space-y-1" data-testid="keybook-note-view">
                      <div className="flex items-center justify-between">
                        <label className="block text-[10px] themed-text-muted uppercase tracking-wider">{t('fields.note')}</label>
                        {selectedEntry.credentialType === 'github' && (
                          <button type="button" onClick={() => handleToggleReveal(selectedEntry.id)} className="text-[11px] themed-text-muted hover:themed-text inline-flex items-center gap-1">
                            {show ? <><EyeOff className="w-3 h-3" />{t('fields.hideNote')}</> : <><Eye className="w-3 h-3" />{t('fields.showNote')}</>}
                          </button>
                        )}
                      </div>
                      {show ? (
                        <p className="kbg-well kb-note-well text-sm themed-text whitespace-pre-wrap break-words px-2.5 py-1.5 max-h-40 overflow-y-auto">{note}</p>
                      ) : (
                        <div className="kbg-well kb-note-well px-2.5 py-1.5 text-sm font-mono themed-text-muted select-none">••••••••••••</div>
                      )}
                    </div>
                  )
                })()}

                {/* Reveal + Edit paired in a 50/50 row. Reveal
                    lazy-decrypts and flips all field values at once;
                    Edit opens the dialog. github gets Refresh Repo +
                    Manage on GitHub instead. */}
                {(() => {
                  if (selectedEntry.credentialType === 'github') {
                    return (
                      <GithubActionRow
                        fields={decryptedData?.fields ?? null}
                        entryId={selectedEntry.id}
                        tenantId={tenantId}
                        canRefresh={canWriteSelected}
                        onRefreshed={() => {
                          // Refresh writes new ciphertext server-side
                          // (fresh repo list, possibly backfilled
                          // authorized_user_login). Invalidate the
                          // local decrypt cache so the auto-decrypt
                          // useEffect re-fetches and the modal
                          // re-renders with the new fields.
                          setDecryptedData(null)
                          decryptInFlight.current = null
                          decryptGen.current++
                          setDecryptEpoch(n => n + 1)
                          keybook.refreshEntries()
                        }}
                      />
                    )
                  }
                  const fields = getDisplayFieldsForType(selectedEntry.credentialType, decryptedData)
                  const showReveal = fields.length > 0
                  return (
                    <div className={`grid ${showReveal && canWriteSelected ? 'grid-cols-2' : 'grid-cols-1'} gap-2 pt-1`}>
                      {showReveal && (
                        <button
                          type="button"
                          onClick={() => handleToggleReveal(selectedEntry.id)}
                          className="kbg-btn px-3 py-2 text-sm"
                        >
                          {revealAll
                            ? <><EyeOff className="w-4 h-4" />Hide</>
                            : <><Eye className="w-4 h-4" />Reveal</>}
                        </button>
                      )}
                      {canWriteSelected && (
                      <button
                        onClick={() => handleEdit(selectedEntry)}
                        className="kbg-btn px-3 py-2 text-sm"
                      >
                        <Pencil className="w-4 h-4" />
                        {t('edit')}
                      </button>
                      )}
                    </div>
                  )
                })()}
              </section>

              {/* Exposure — who might have seen this value since it was saved:
                  "This key might be exposed to Organization owner and N others"
                  (model C: roles — the owner first —, teams, people shared with
                  directly or who opened it, apps, outside orgs). */}
              {showExposure && exposure?.entryId === selectedEntry.id && exposureHeadlineParts(exposure.data) && (
                  <section className="kbg-well px-3 py-2.5 flex items-center gap-2" data-testid="keybook-exposure-line">
                    <ExposureHeadline data={exposure.data} className="flex-1 min-w-0 text-xs themed-text leading-snug" />
                    <button
                      type="button"
                      data-testid="exposure-view"
                      onClick={() => setExposureOpen({ id: selectedEntry.id, label: selectedEntry.sourceLabel ?? selectedEntry.label })}
                      className="kbg-btn px-3 h-7 text-xs shrink-0"
                    >
                      <Users className="w-3.5 h-3.5" />
                      {t('exposure.view')}
                    </button>
                  </section>
              )}

              </div>
              <div className="space-y-4 min-w-0">
              {/* Action row — Access (org only, accent = the share affordance),
                  Ownership/Claim, Delete. Gated by manage permission (server
                  re-checks on every mutation). */}
              {(() => {
                const isCallerOwner = selectedEntry.ownerType === 'email'
                  && !!callerEmailLower
                  && selectedEntry.ownerId === callerEmailLower
                const isOrphan = selectedEntry.ownerType !== 'role' && !selectedEntry.ownerActive
                const canManage = selectedEntry.permission === 'manage'
                // Write — or Read with manage_keybook_permission — sees the access
                // list read-only (D27); every other action here needs Manage.
                if (!canManage) {
                  if (!isOrg || !canSeeKeybookAccess(selectedEntry.permission, keybook.accessCapability)) return null
                  return (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSharingEntry({ id: selectedEntry.id, label: selectedEntry.sourceLabel ?? selectedEntry.label, credentialType: selectedEntry.credentialType, readOnly: true })}
                        className="kbg-btn px-3 h-8 text-xs"
                        data-testid="action-access-view"
                      >
                        <Users className="w-3.5 h-3.5" />
                        {t('access.viewItem')}
                      </button>
                    </div>
                  )
                }
                return (
                  <div className="flex flex-wrap gap-2">
                    {isOrg && canManage && (
                      <button
                        onClick={() => setSharingEntry({ id: selectedEntry.id, label: selectedEntry.sourceLabel ?? selectedEntry.label, credentialType: selectedEntry.credentialType })}
                        className="themed-btn-primary px-3 h-8 rounded-[10px] text-xs flex items-center gap-1.5 cursor-pointer"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Access
                      </button>
                    )}
                    {(() => {
                      if (isOrphan && canManage) {
                        return (
                          <button
                            onClick={() => setTransferring({
                              id: selectedEntry.id,
                              label: selectedEntry.label,
                              currentOwner: { type: 'email', id: selectedEntry.ownerId },
                              mode: 'claim',
                            })}
                            data-testid="action-claim"
                            className="kbg-btn px-3 h-8 text-xs text-amber-400"
                          >
                            <Hand className="w-3.5 h-3.5" />
                            Claim
                          </button>
                        )
                      }
                      if (canManage) {
                        // caller is owner → transfer; a manager acting on
                        // someone else's active key → reassign.
                        const mode = isCallerOwner ? 'transfer' : 'reassign'
                        return (
                          <button
                            onClick={() => setTransferring({
                              id: selectedEntry.id,
                              label: selectedEntry.label,
                              currentOwner: { type: selectedEntry.ownerType, id: selectedEntry.ownerId },
                              mode,
                            })}
                            data-testid="action-transfer"
                            className="kbg-btn px-3 h-8 text-xs"
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5" />
                            Ownership
                          </button>
                        )
                      }
                      return null
                    })()}
                    {canManage && (
                    <button
                      onClick={() => handleDelete(selectedEntry.id, selectedEntry.label)}
                      className="ml-auto px-3 h-8 rounded-[10px] text-xs flex items-center gap-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t('delete')}
                    </button>
                    )}
                  </div>
                )
              })()}

              {/* ── Collection filing — a key files into one collection
                     (a grant scope, §5d) ── */}
              {canManageSelected && (
                <section className="kbg-well px-3.5 py-2.5">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Folder className="w-3.5 h-3.5 themed-text-muted" />
                    <span className="text-[10px] themed-text-muted uppercase tracking-wider">
                      {t('inspector.collectionTitle')}
                    </span>
                  </div>
                  <select
                    data-testid="entry-collection-select"
                    value={selectedEntry.collectionId ?? ''}
                    onChange={e => void handleSetCollection(selectedEntry.id, e.target.value || null)}
                    className="themed-input w-full text-xs rounded px-2 py-1.5 cursor-pointer"
                    title={t('inspector.collectionHint')}
                  >
                    <option value="">No collection</option>
                    {collectionOptions.filter(c => c.permission !== 'read').map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </section>
              )}

              {/* ── Bindings — files/folders this key is plugged into,
                     with per-binding health ── */}
              <section className="kbg-well px-3.5 py-2.5">
                <div className="flex items-center gap-1.5 mb-2">
                  <FolderInput className="w-3.5 h-3.5 themed-text-muted" />
                  <span className="text-[10px] themed-text-muted uppercase tracking-wider">
                    {t('inspector.usedByTitle')}
                  </span>
                </div>
                {(selectedEntry.fileIds?.length ?? 0) > 0 ? (
                  <ul className="space-y-0.5 mb-2">
                    {selectedEntry.fileIds!.map(fid => {
                      const meta = filePaths[fid]
                      const name = meta?.name ?? fid
                      const isFolder = !!meta?.isFolder
                      const allSegments = (meta?.path ?? '').replace(/^P:\//, '').split('/').filter(Boolean)
                      const parentSegments = allSegments.slice(0, -1)
                      const usage = selectedEntry.files?.find(f => f.fileId === fid)
                      // Three states drive the badge:
                      //   1. last failure note set → most recent attempt failed
                      //   2. last_success_at set → currently working
                      //   3. neither → never used
                      const failed = !!usage?.lastFailureReason
                      const usedAgo = formatAgo(usage?.lastUsedAt)
                      const succeededAgo = formatAgo(usage?.lastSuccessAt)
                      return (
                        <li
                          key={fid}
                          className="group flex items-start justify-between gap-2 px-1.5 py-1.5 -mx-1.5 rounded-md hover:bg-white/5 transition-colors"
                          data-testid="entry-file-chip"
                        >
                          <div className="flex-1 min-w-0">
                            {parentSegments.length > 0 && (
                              <div className="text-[11px] themed-text-muted truncate">
                                <BreadcrumbPath
                                  path={`P:/${parentSegments.join('/')}`}
                                  separator="/"
                                  trailingSeparator
                                  compact
                                />
                              </div>
                            )}
                            <div className="flex items-center gap-1.5">
                              <FileIcon name={name} isFolder={isFolder} size={14} />
                              <span className="text-sm themed-text truncate" title={name}>{name}</span>
                            </div>
                            {(failed || succeededAgo) && (
                              <div
                                className={`mt-0.5 ml-[11px] flex items-center gap-1 text-[11px] min-w-0 ${
                                  failed ? 'text-amber-400' : 'text-emerald-400/80'
                                }`}
                              >
                                {failed ? (
                                  <>
                                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                    <Tooltip
                                      content={usage?.lastFailureReason ?? ''}
                                      className="truncate min-w-0"
                                    >
                                      Failed {usedAgo}
                                      {usage?.lastFailureReason ? `: ${usage.lastFailureReason}` : ''}
                                    </Tooltip>
                                  </>
                                ) : (
                                  <>
                                    <Check className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">Used {succeededAgo}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          {canWriteSelected && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await keybook.unbindFile(selectedEntry.id, fid)
                              } catch (err: any) {
                                toast.error(err.message || 'Failed to unbind file')
                              }
                            }}
                            title="Unbind"
                            className="p-1 themed-text-muted hover:text-red-400 transition-colors kbg-reveal cursor-pointer"
                          >
                            <X size={14} />
                          </button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className="text-xs themed-text-muted py-1 mb-2">
                    {t('inspector.noBindings')}
                  </p>
                )}

                {FilePickerDialog && canWriteSelected && (
                  <button
                    type="button"
                    onClick={() => setAddingFileFor(selectedEntry.id)}
                    className="w-full px-2 py-1.5 rounded-md text-xs themed-text-muted hover:themed-text hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    data-testid="entry-add-file"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add file or folder
                  </button>
                )}
              </section>

              {/* ── Activity — the entry audit log. Manage-only (the
                     server 403s below manage); lazily fetched on first
                     expand so the common browse path pays nothing ── */}
              {canManageSelected && (
                <section className="kbg-well">
                  <button
                    type="button"
                    onClick={() => void handleToggleActivity(selectedEntry.id)}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left cursor-pointer rounded-xl hover:bg-white/5 transition-colors"
                    aria-expanded={activityOpen}
                    data-testid="entry-activity-toggle"
                  >
                    <History className="w-3.5 h-3.5 themed-text-muted" />
                    <span className="text-[10px] themed-text-muted uppercase tracking-wider flex-1">
                      {t('activity.title')}
                    </span>
                    <span className="text-xs themed-text-muted">{activityOpen ? '−' : '+'}</span>
                  </button>
                  {activityOpen && (
                    <div className="px-3.5 pb-3">
                      {auditLoading ? (
                        <div className="flex items-center justify-center py-4 themed-text-muted">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </div>
                      ) : !auditEvents || auditEvents.length === 0 ? (
                        <p className="text-xs themed-text-muted py-1">{t('activity.empty')}</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {auditEvents.map(ev => {
                            const key = AUDIT_EVENT_KEYS[ev.eventType]
                            const label = key ? t(`activity.events.${key}`) : ev.eventType
                            // Best-effort detail from the jsonb payload —
                            // grants carry recipient + permission, files
                            // carry fileId, ownership carries the new owner.
                            const detail = ev.newValue?.recipientId
                              ? `${ev.newValue.recipientId}${ev.newValue.permission ? ` · ${ev.newValue.permission}` : ''}`
                              : ev.newValue?.ownerId
                                ? ev.newValue.ownerId
                                : ev.newValue?.fileId
                                  ? (filePaths[ev.newValue.fileId]?.name ?? ev.newValue.fileId)
                                  : ev.newValue?.collectionId !== undefined
                                    ? (collectionOptions.find(c => c.id === ev.newValue.collectionId)?.name
                                        ?? (ev.newValue.collectionId ? ev.newValue.collectionId : t('activity.noCollection')))
                                    : null
                            return (
                              <li key={ev.id} className="flex items-baseline gap-2 text-xs min-w-0">
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 translate-y-[-1px]" style={{ background: 'var(--kb-gold, #d4a843)' }} aria-hidden />
                                <span className="themed-text whitespace-nowrap">{label}</span>
                                {detail && <span className="themed-text-muted truncate min-w-0">{detail}</span>}
                                <span className="themed-text-muted ml-auto whitespace-nowrap flex-shrink-0">
                                  {formatAgo(ev.eventAt) ?? new Date(ev.eventAt).toLocaleDateString()}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </section>
              )}
              </div>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {showCreateDialog && (
        <EntryCreateDialog
          tenantId={tenantId}
          onClose={() => setShowCreateDialog(false)}
          onSave={async (label, data, options) => {
            try {
              const newId = await keybook.createEntry(label, data, options)
              setShowCreateDialog(false)
              // Show the new key: a search or an open collection could hide it (it lands unfiled), so step out of
              // both, then scroll to it and focus it (the effect below, once its fob is on the rack).
              setSearchQuery('')
              setCollectionFilter('all')
              setFocusEntryId(newId)
              toast.success(t('actions.saved'))
            } catch (err: any) {
              toast.error(err.message || 'Failed to save')
            }
          }}
        />
      )}
      {editingEntry && (
        <EntryCreateDialog
          // A Reload after a conflict reopens the editor on the current value.
          key={`${editingEntry.id}:${editingEntry.version}`}
          tenantId={tenantId}
          editTitle={t('edit')}
          // The SHARED label, never the caller's private nickname (`label` is
          // customLabel ?? sourceLabel) — and sent only when it changed, so a
          // value edit never renames the key for everyone.
          editLabel={editingEntry.meta.sourceLabel ?? editingEntry.meta.label}
          editProvider={editingEntry.meta.provider ?? undefined}
          editData={editingEntry.data}
          editEntryId={editingEntry.id}
          labelLocked={editingEntry.meta.permission !== 'manage'}
          changedSinceOpened={!!editingEntry.changed}
          onReloadChanged={async () => {
            const openedFor = editingEntry
            try {
              const read = await keybook.readEntryForEdit(openedFor.id)
              setEditingEntry(cur => (cur === openedFor ? { ...openedFor, data: read.data, version: read.version, changed: false } : cur))
            } catch (err: any) {
              toast.error(err.message || 'Failed to read entry')
            }
          }}
          onClose={() => setEditingEntry(null)}
          onSave={async (label, data, options) => {
            try {
              const shared = editingEntry.meta.sourceLabel ?? editingEntry.meta.label
              savesInFlight.current.add(editingEntry.id)
              await keybook.updateEntry(editingEntry.id, data, {
                ...(label !== shared && editingEntry.meta.permission === 'manage' && { label }),
                usernameHint: options?.usernameHint,
                expectedVersion: editingEntry.version,
                noteOnly: options?.noteOnly,
              })
              // Close THIS opening's dialog only — an edit opened meanwhile
              // (another key, or this key reopened) stays open. `editingEntry`
              // is the state object this dialog was opened with, new per opening.
              const openedFor = editingEntry
              setEditingEntry(cur => (cur === openedFor ? null : cur))
              toast.success(t('actions.updated'))
            } catch (err: any) {
              if (err?.code === 'KEYBOOK_ENTRY_CHANGED') {
                // Keep the dialog — and the person's draft — open, and say why.
                const openedFor = editingEntry
                setEditingEntry(cur => (cur === openedFor ? { ...openedFor, changed: true } : cur))
              } else {
                toast.error(err.message || 'Failed to update')
              }
            } finally {
              savesInFlight.current.delete(editingEntry.id)
              // Invalidate the local decryption cache for the edited entry —
              // otherwise the next Reveal shows pre-edit values. Also on a
              // failure: the value may have landed before a refused rename.
              if (selectedEntryIdRef.current === editingEntry.id) {
                setDecryptedData(null)
                setRevealAll(false)
                decryptInFlight.current = null
                decryptGen.current++
                setDecryptEpoch(n => n + 1)
              }
            }
          }}
        />
      )}
      {sharingEntry && (
        <KeyAccessDialog
          subject={{ kind: 'entry', entryId: sharingEntry.id, label: sharingEntry.label, credentialType: sharingEntry.credentialType }}
          readOnly={!!sharingEntry.readOnly}
          tenantId={tenantId}
          callerEmail={userEmail}
          callerRole={userRole}
          isOrg={!!isOrg}
          onClose={() => { setSharingEntry(null); setExposureTick(n => n + 1) }}
        />
      )}
      {exposureOpen && (
        <KeyExposureDialog
          entryId={exposureOpen.id}
          label={exposureOpen.label}
          onClose={() => setExposureOpen(null)}
        />
      )}
      {showRootAccess && (
        <KeyAccessDialog
          subject={{ kind: 'root', label: t('settings.title') }}
          tenantId={tenantId}
          callerEmail={userEmail}
          callerRole={userRole}
          onClose={() => { setShowRootAccess(false); keybook.refreshEntries() }}
        />
      )}
      {sharingCollection && (
        <KeyAccessDialog
          subject={{ kind: 'collection', collectionId: sharingCollection.id, label: sharingCollection.name }}
          readOnly={!!sharingCollection.readOnly}
          tenantId={tenantId}
          callerEmail={userEmail}
          callerRole={userRole}
          isOrg={!!isOrg}
          onClose={() => setSharingCollection(null)}
        />
      )}
      {/* The collection tile's menu — the shared glass menu; it portals
          itself to <body>, so the theme class rides on it. */}
      {collectionMenu && (
        <FileContextMenu
          x={collectionMenu.x}
          y={collectionMenu.y}
          items={collectionMenuItems(collectionMenu)}
          menuRef={collectionMenuRef}
          className={`kb-deck-portal ${themeClass}`}
          testId="collection-context-menu"
        />
      )}
      {transferring && (
        <TransferOwnerDialog
          entryId={transferring.id}
          entryLabel={transferring.label}
          tenantId={tenantId}
          currentOwner={transferring.currentOwner}
          mode={transferring.mode}
          callerEmail={userEmail}
          isOrg={!!isOrg}
          onClose={() => setTransferring(null)}
          onTransferred={() => { void keybook.refreshEntries(); setSelectedEntryId(null) }}
        />
      )}
      {/* Portaled for the same taskbar-stacking reason (the picker is
          fixed z-60 but in-tree it would bury under the portaled modal). */}
      {addingFileFor && FilePickerDialog && createPortal(
        // The picker is z-[60] — a stacking context above the keybook modal band.
        <div className="kb-deck-portal relative z-[110]">
        <FilePickerDialog
          tenantId={tenantId}
          title="Bind a file or folder"
          onSelect={async (id, _isFolder) => {
            const entryId = addingFileFor
            setAddingFileFor(null)
            // The server re-decides Write at the act: a viewer lowered while
            // the picker was open gets its refusal here, as a toast.
            try {
              await keybook.bindFile(entryId, id)
            } catch (err: any) {
              toast.error(err.message || 'Failed to bind file')
            }
          }}
          onClose={() => setAddingFileFor(null)}
        />
        </div>,
        document.body,
      )}
    </div>
  )
}

/** Helper: derive the github.com management URL for an installation. */
function githubManageUrl(fields: Record<string, string> | null): string | null {
  if (!fields) return null
  const installationId = fields.installation_id
  if (!installationId) return null
  const account = fields.account_login || 'unknown'
  const accountType = fields.account_type || 'User'
  return accountType === 'Organization'
    ? `https://github.com/organizations/${account}/settings/installations/${installationId}`
    : `https://github.com/settings/installations/${installationId}`
}

/** Read-only summary for a github (App OAuth) entry. The
 *  access_token / refresh_token are NEVER rendered. The metadata
 *  here (account, scope label, installation id, repo names) is what
 *  GitHub itself shows on the install page; we don't add Reveal
 *  gating because there's nothing here that isn't accessible to
 *  anyone with the token (which the user already has, since they
 *  authorized it). */
function GithubInstallationSummary({ fields }: { fields: Record<string, string> | null }) {
  const { productName } = useBrand()
  if (!fields) {
    return <p className="text-xs themed-text-muted">Loading installation metadata…</p>
  }

  const account = fields.account_login || 'unknown'
  const accountType = fields.account_type || 'User'
  const installationId = fields.installation_id
  const authorizedUserLogin = fields.authorized_user_login
  const repoSelection = fields.repository_selection
  const repoCount = parseInt(fields.repository_count || '0', 10)
  const repoNames: string[] = (() => {
    try { return JSON.parse(fields.repository_names || '[]') } catch { return [] }
  })()
  const repoTruncated = fields.repository_truncated === '1'
  const scopeLabel = repoSelection === 'all'
    ? `All repositories${repoCount ? ` (${repoCount} accessible)` : ''}`
    : repoSelection === 'selected'
      ? `Selected${repoCount ? ` (${repoCount} repo${repoCount === 1 ? '' : 's'})` : ''}`
      : repoCount > 0 ? `${repoCount} repo${repoCount === 1 ? '' : 's'}` : 'Unknown'

  return (
    <div className="space-y-3">
      {/* Acting-as identity in a blue-tinted wash. Sits OUTSIDE the
          account well because it's an attribute of THIS entry —
          switching identity means creating a fresh entry, not
          editing this one — and has no Papan-side action. */}
      {authorizedUserLogin && (
        <div className="rounded-[10px] bg-blue-500/12 px-3 py-2 flex items-center gap-2 text-xs">
          <AtSign size={13} className="text-blue-400 flex-shrink-0" />
          <span className="themed-text-muted">Acting as</span>
          <span className="themed-text font-medium">@{authorizedUserLogin}</span>
        </div>
      )}

      <div className="kbg-well p-3 space-y-3">
        <div>
          <div className="text-[10px] themed-text-muted uppercase tracking-wider mb-0.5">
            Authorized GitHub account
          </div>
          <div className="flex items-baseline gap-2 flex-wrap min-w-0">
            <h3 className="text-base font-semibold themed-text truncate" title={account}>
              {account}
            </h3>
            <span className="text-xs themed-text-muted">({accountType})</span>
          </div>
        </div>

        {/* Shared-install note — before the scope/install details. */}
        <p className="text-[11px] themed-text-muted leading-relaxed">
          Shared install — github.com controls repo scope for everyone using <strong>{account}</strong> on {productName}.
          {accountType === 'Organization' && <> Other org admins can change or uninstall.</>}
          {' '}To switch acting identity, create a new entry — actors aren&apos;t editable in place.
        </p>

        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
          <dt className="themed-text-muted">Scope</dt>
          <dd className="themed-text">{scopeLabel}</dd>
          {installationId && <>
            <dt className="themed-text-muted">Installation</dt>
            <dd className="themed-text font-mono">#{installationId}</dd>
          </>}
        </dl>

        {repoNames.length > 0 && (
          <div>
            <div className="text-[10px] themed-text-muted uppercase tracking-wider mb-1">
              Repos
            </div>
            <div className="max-h-32 overflow-y-auto">
              <ul className="space-y-0.5">
                {repoNames.slice(0, 8).map(name => (
                  <li key={name} className="group flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-white/5 transition-colors">
                    <span className="themed-text-muted text-[11px] flex-shrink-0" aria-hidden>•</span>
                    <span className="font-mono text-[11px] themed-text truncate flex-1 min-w-0" title={name}>{name}</span>
                    {/* Per-row "open on github.com" — quicker than
                        opening Manage and navigating, and obviously
                        scoped to the specific repo. */}
                    <button
                      type="button"
                      onClick={() => window.open(`https://github.com/${name}`, '_blank', 'noopener,noreferrer')}
                      className="p-0.5 themed-text-muted hover:themed-text rounded transition-colors kbg-reveal flex-shrink-0 cursor-pointer"
                      aria-label={`Open ${name} on GitHub`}
                      title={`Open ${name} on GitHub`}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </li>
                ))}
                {(repoNames.length > 8 || repoTruncated) && (
                  <li className="px-1.5 py-1 themed-text-muted text-[11px]">
                    {repoTruncated && repoNames.length <= 8
                      ? '…list truncated'
                      : `…+${repoNames.length - 8} more`}
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** Bottom action row for a github entry. Replaces the standard
 *  Reveal + Edit pair: Reveal has nothing to reveal here (the token
 *  isn't rendered, the metadata is public-ish), and the Edit dialog
 *  for github just echoes the same metadata that's already in the
 *  modal above. So we use the slot for the two genuinely useful
 *  github-flavoured actions instead. */
function GithubActionRow({
  fields,
  entryId,
  tenantId,
  canRefresh,
  onRefreshed,
}: {
  fields: Record<string, string> | null
  entryId: string
  tenantId: string
  /** Refresh writes the key (write or manage). */
  canRefresh: boolean
  onRefreshed: () => void
}) {
  const { t } = useTranslation('keybook')
  const [refreshing, setRefreshing] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  // Listen for install completion broadcasts so the "Refresh
  // failed — opening Reauthorize…" note clears once the popup
  // round-trip succeeds. KeybookView's listener handles list +
  // decryptedData invalidation; this one is just for the local
  // note state inside this row.
  useEffect(() => {
    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel('papan-github-install')
      ch.onmessage = (ev) => {
        if (ev.data?.type === 'installed') setNote(null)
      }
    } catch { /* unsupported — postMessage handled in KeybookView */ }
    return () => ch?.close()
  }, [])

  // Reauthorize: same flow as the initial Authorize button — opens
  // the GitHub OAuth popup. The callback's upsert-by-installation_id
  // matches this entry and rewrites the stored tokens with fresh
  // ones. Auto-fires when Refresh Repo fails — most refresh
  // failures (revoked / stale / never-had-refresh-token cases) only
  // recover via a fresh OAuth grant, so there's no value in making
  // the user click a separate button.
  const openReauthorizePopup = () => {
    const apiBase = getApiBase()
    const origin = window.location.origin
    // /github/start is now always OAuth-first. Single-install users
    // auto-pick; multi-install users get a Papan-side picker page.
    const url = `${apiBase}/api/keybook/oauth/github/start?tenantId=${encodeURIComponent(tenantId)}&origin=${encodeURIComponent(origin)}`
    window.open(url, 'papan-github-install', 'width=1020,height=940')
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    setNote(null)
    try {
      const { keybookApi } = await import('../client/api.js')
      const r = await keybookApi.refreshGithubMetadata(entryId)
      setNote(`✓ Refreshed — ${r.repositoryCount} repo${r.repositoryCount === 1 ? '' : 's'}${r.repositoryTruncated ? ' (list truncated)' : ''}`)
      onRefreshed()
    } catch (err: any) {
      // The key was changed (another refresh, a re-authorization, a rotation)
      // while this one ran — its token is fine, so no Reauthorize: reload the
      // fields and let the user refresh again.
      if (err?.code === 'KEYBOOK_ENTRY_CHANGED') {
        setNote('The key changed while refreshing — refresh again.')
        onRefreshed()
        return
      }
      // Refresh-metadata server-side already does refresh-token
      // retry on first-call failure. Reaching this catch means the
      // token chain is unrecoverable — open the OAuth popup so the
      // user can re-grant in one click. Brief delay so the inline
      // note is readable before the popup focus-steals.
      setNote(`Refresh failed (${err?.message ?? String(err)}). Opening Reauthorize…`)
      window.setTimeout(() => openReauthorizePopup(), 600)
    } finally {
      setRefreshing(false)
    }
  }

  const manageUrl = githubManageUrl(fields)

  return (
    <div className="space-y-2 pt-1">
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing || !canRefresh}
          title={canRefresh ? undefined : t('refreshNeedsWrite')}
          className="kbg-btn px-3 py-2 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh Repo'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (manageUrl) {
              window.open(manageUrl, 'papan-github-install', 'width=1020,height=940')
            }
          }}
          disabled={!manageUrl}
          className="kbg-btn px-3 py-2 text-sm"
          title={manageUrl ? 'Manage repos / permissions / uninstall on github.com.' : 'No installation id — re-authorize to populate.'}
        >
          <ExternalLink className="w-4 h-4" />
          Manage on GitHub
        </button>
      </div>
      {note && (
        <p className={`text-xs ${note.startsWith('✓') ? 'themed-text-secondary' : 'text-red-400'}`}>
          {note}
        </p>
      )}
    </div>
  )
}
